import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from '../Header/Header';
import { globalLogout } from '../../Utils/Logout';
// Loader Component
const Loader = ({ fullScreen = false }) => {
  return (
    <div className={`flex items-center justify-center ${fullScreen ? 'h-screen w-screen' : 'h-full w-full'}`}>
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );
};

// Error Message Component
const ErrorMessage = ({ message, onRetry }) => {
  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-red-50 border-l-4 border-red-500 p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">
              {message}
              {onRetry && (
                <button 
                  onClick={onRetry}
                  className="ml-2 text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none"
                >
                  Retry
                </button>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const OrderDetails = () => {
  const { orderItemId } = useParams();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = Cookies.get("ecom_user_token");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setOrderData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to fetch order details");
       globalLogout(err)
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderDetails();
  }, [orderItemId]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const handleCancelOrder = async () => {
    if (!orderData?.product) return;
    
    try {
      setCancelling(true);
      const token = Cookies.get("ecom_user_token");
      const response = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/buyer/cancel-product-by-buyer/${orderData.product.orderId}`,
        {
          productId: orderData.product.product.productId,
          variantId: orderData.product.product.variantId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success(response.data.message);
      // Refresh order details after cancellation
      await fetchOrderDetails();
    } catch (err) {
    globalLogout(err)
    } finally {
      setCancelling(false);
      setShowCancelModal(false);
    }
  };

  const getTrackingSteps = () => {
    const steps = [
      { 
        id: 'ordered', 
        name: 'Order Placed', 
        description: 'Your order has been received',
        apiStatus: 'pending',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7 7v18" />
          </svg>
        )
      },
      { 
        id: 'confirmed', 
        name: 'Order Confirmed', 
        description: 'Seller has accepted your order',
        apiStatus: 'confirmed',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      },
      { 
        id: 'shipped', 
        name: 'Shipped', 
        description: 'Seller has shipped your item',
        apiStatus: 'shipped',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        )
      },
      { 
        id: 'in_transit', 
        name: 'In Transit', 
        description: 'Your item is on the way',
        apiStatus: 'in_transit',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        )
      },
      { 
        id: 'delivered', 
        name: 'Delivered', 
        description: 'Your item has arrived',
        apiStatus: 'delivered',
        icon: (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      }
    ];

    let currentStepIndex = 0;
    
    if (orderData?.product) {
      if (orderData.product.product.status === 'cancelled') {
        // If order is cancelled, show all steps as incomplete
        return steps.map(step => ({
          ...step,
          status: 'incomplete',
          current: false
        }));
      }

      if (orderData.product.product.status === 'confirmed') {
        currentStepIndex = 1;
      }

      if (orderData.product.tracking) {
        switch (orderData.product.tracking.status) {
          case 'shipped':
            currentStepIndex = 2;
            break;
          case 'in_transit':
            currentStepIndex = 3;
            break;
          case 'delivered':
            currentStepIndex = 4;
            break;
          case 'not_shipped':
            currentStepIndex = orderData.product.product.status === 'confirmed' ? 1 : 0;
            break;
          default:
            break;
        }
      }
    }

    return steps.map((step, index) => ({
      ...step,
      status: index <= currentStepIndex ? 'complete' : 'incomplete',
      current: index === currentStepIndex
    }));
  };

  if (loading) return <Loader fullScreen />;
  if (error) return <ErrorMessage message={error} onRetry={fetchOrderDetails} />;
  if (!orderData) return <ErrorMessage message="Order not found" />;

  const order = orderData.product;
  const trackingSteps = getTrackingSteps();
  const isCOD = order.payment.method === 'cod';
  const isCancelled = order.product.status === 'cancelled';
  const canCancel = !isCancelled && 
                   order.tracking?.status !== 'delivered' && 
                   order.product.status !== 'delivered';
  const showRefundInfo = isCancelled && !isCOD;
  const paymentIncomplete = isCOD && order.payment.status !== 'completed';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Cancel Confirmation Modal */}
          {showCancelModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-900">Confirm Cancellation</h3>
                    <button 
                      onClick={() => setShowCancelModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="mb-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mt-1">
                        <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-gray-700">
                          Are you sure you want to cancel this order? 
                          This action cannot be undone.
                        </p>
                        {order.tracking?.status === 'shipped' && (
                          <p className="text-gray-600 mt-2 text-sm">
                            Note: Since your order has already been shipped, you may need to return the item.
                          </p>
                        )}
                        {isCOD && (
                          <p className="text-gray-600 mt-2 text-sm">
                            Since you paid via Cash on Delivery, no refund will be processed.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowCancelModal(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Go Back
                    </button>
                    <button
                      onClick={handleCancelOrder}
                      disabled={cancelling}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                    >
                      {cancelling ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Cancelling...
                        </>
                      ) : (
                        'Confirm Cancellation'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mb-6">
            <button 
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Orders
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
                  <p className="text-gray-500 mt-1">Order ID: {order.orderId}</p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    order.product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    order.product.status === 'delivered' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {order.product.status.charAt(0).toUpperCase() + order.product.status.slice(1)}
                  </span>
                  {canCancel && (
                    <button
                      onClick={() => setShowCancelModal(true)}
                      disabled={cancelling}
                      className="px-4 py-2 bg-white border border-red-500 text-red-600 rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Cancel Order</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Cancellation Banner if order is cancelled */}
            {isCancelled && (
              <div className="bg-red-50 border-l-4 border-red-500 p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <strong className="font-medium">This order has been cancelled</strong>
                    {showRefundInfo ? (
                      <p className="text-sm mt-1">
                        {order.product.isRefunded ? (
                          <>
                            Your refund of {formatCurrency(order.product.refundedAmount)} was processed on {formatDate(order.product.refundedAt)}.
                            {order.product.razorpayRefundId && (
                              <span> Refund ID: {order.product.razorpayRefundId}</span>
                            )}
                          </>
                        ) : (
                          "Your refund is being processed and will be completed soon."
                        )}
                      </p>
                    ) : (
                      <p className="text-sm mt-1">
                        Since you did not pay, so no refund is required.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Payment Incomplete Banner for COD orders */}
            {paymentIncomplete && (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-3 flex-shrink-0 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <strong className="font-medium">Payment Pending</strong>
                    <p className="text-sm mt-1">
                      You will pay {formatCurrency(order.totalAmount)} when your order is delivered.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
              {/* Product Information */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h2>
                  
                  <div className="flex flex-col sm:flex-row gap-5">
                    <div className="flex-shrink-0">
                      <img 
                        src={order.product.images[0]} 
                        alt={order.product.name}
                        className="w-32 h-32 object-cover rounded-lg border border-gray-200"
                      />
                    </div>
                    
                    <div className="flex-grow">
                      <h3 className="text-xl font-medium text-gray-900">{order.product.name}</h3>
                      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <div>
                          <span className="text-gray-500">Quantity:</span>
                          <span className="ml-2 font-medium text-gray-900">{order.product.quantity}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Price:</span>
                          <span className="ml-2 font-medium text-gray-900">{formatCurrency(order.product.price)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Total:</span>
                          <span className="ml-2 font-medium text-gray-900">{formatCurrency(order.product.total)}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Order Date:</span>
                          <span className="ml-2 font-medium text-gray-900">{formatDate(order.orderCreatedAt)}</span>
                        </div>
                        {isCancelled && (
                          <>
                            <div>
                              <span className="text-gray-500">Cancellation Date:</span>
                              <span className="ml-2 font-medium text-gray-900">
                                {order.product.refundedAt ? formatDate(order.product.refundedAt) : formatDate(new Date())}
                              </span>
                            </div>
                            {showRefundInfo && (
                              <div>
                                <span className="text-gray-500">Refund Amount:</span>
                                <span className="ml-2 font-medium text-gray-900">
                                  {formatCurrency(order.product.refundedAmount || order.product.total)}
                                </span>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shipping Information */}
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping & Payment</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-medium text-gray-900 mb-3">Delivery Address</h3>
                      <div className="bg-white p-4 rounded-md border border-gray-200">
                        <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
                        <p className="text-gray-600 mt-1">{order.shippingAddress.addressLine1}</p>
                        {order.shippingAddress.addressLine2 && (
                          <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
                        )}
                        <p className="text-gray-600">
                          {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
                        </p>
                        <p className="text-gray-600">{order.shippingAddress.country}</p>
                        <p className="text-gray-600 mt-2">Phone: {order.shippingAddress.phone}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-medium text-gray-900 mb-3">Seller Information</h3>
                        <div className="bg-white p-4 rounded-md border border-gray-200">
                          <p className="text-gray-900">{order.seller.businessName}</p>
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="font-medium text-gray-900 mb-3">Payment Information</h3>
                        <div className="bg-white p-4 rounded-md border border-gray-200">
                          <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Method:</span>
                            <span className="font-medium text-gray-900">{order.payment.method.toUpperCase()}</span>
                          </div>
                          <div className="flex justify-between mb-2">
                            <span className="text-gray-600">Status:</span>
                            <span className={`font-medium ${
                              order.payment.status === 'completed' ? 'text-green-600' : 
                              order.payment.status === 'failed' ? 'text-red-600' : 
                              'text-yellow-600'
                            }`}>
                              {isCOD && order.payment.status === 'completed' ? 'Pending' : order.payment.status}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Amount:</span>
                            <span className="font-medium text-gray-900">{formatCurrency(order.totalAmount)}</span>
                          </div>
                          {showRefundInfo && order.sellerRefund && (
                            <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                              <h4 className="font-medium text-gray-900">Refund Details</h4>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Refund Status:</span>
                                <span className="font-medium text-green-600">
                                  {order.sellerRefund.isRefunded ? 'Completed' : 'Processing'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600">Refund Amount:</span>
                                <span className="font-medium text-gray-900">
                                  {formatCurrency(order.sellerRefund.amount)}
                                </span>
                              </div>
                              {order.sellerRefund.refundedAt && (
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Refund Date:</span>
                                  <span className="font-medium text-gray-900">
                                    {formatDate(order.sellerRefund.refundedAt)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tracking Progress */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-lg p-5 border border-gray-200 sticky top-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h2>
                  
                  <div className="space-y-6">
                    {/* Progress Bar */}
                    <div className="relative">
                      {/* Progress line */}
                      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      
                      {trackingSteps.map((step, index) => (
                        <div key={step.id} className="relative flex items-start pb-8 last:pb-0">
                          <div className={`absolute left-5 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
                            <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
                          </div>
                          
                          <div className={`relative flex items-center justify-center w-10 h-10 rounded-full ${step.current ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${
                            step.status === 'complete' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
                          }`}>
                            {step.status === 'complete' ? (
                              step.icon
                            ) : (
                              <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
                                {index + 1}
                              </span>
                            )}
                          </div>
                          
                          <div className="ml-4 pt-1">
                            <h3 className={`text-sm font-medium ${
                              step.current ? 'text-blue-600' : 
                              step.status === 'complete' ? 'text-gray-900' : 'text-gray-500'
                            }`}>
                              {step.name}
                            </h3>
                            <p className={`text-xs ${
                              step.current ? 'text-blue-500' : 'text-gray-400'
                            }`}>
                              {step.description}
                            </p>
                            {step.current && (
                              <p className="text-xs text-blue-500 mt-1 font-medium">Current Status</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Tracking Details */}
                    {order.tracking && order.product.status !== 'cancelled' && (
                      <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
                        <h3 className="font-medium text-blue-800 mb-2 flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          Tracking Details
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-blue-700">Carrier:</span>
                            <span className="font-medium text-blue-900">{order.tracking.carrier || 'Not specified'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-blue-700">Status:</span>
                            <span className="font-medium text-blue-900 capitalize">{order.tracking.status || 'Not available'}</span>
                          </div>
                          {order.tracking.awbNumber && (
                            <div className="flex justify-between">
                              <span className="text-blue-700">AWB #:</span>
                              <span className="font-medium text-blue-900">{order.tracking.awbNumber}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Refund Summary for Cancelled Orders */}
                    {showRefundInfo && (
                      <div className="bg-green-50 p-4 rounded-md border border-green-100">
                        <h3 className="font-medium text-green-800 mb-2 flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Refund Summary
                        </h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-green-700">Status:</span>
                            <span className="font-medium text-green-900">
                              {order.product.isRefunded ? 'Refund Completed' : 'Refund Processing'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-700">Amount:</span>
                            <span className="font-medium text-green-900">
                              {formatCurrency(order.product.refundedAmount || order.product.total)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-700">Processed On:</span>
                            <span className="font-medium text-green-900">
                              {formatDate(order.product.refundedAt || new Date())}
                            </span>
                          </div>
                          {order.product.razorpayRefundId && (
                            <div className="flex justify-between">
                              <span className="text-green-700">Refund ID:</span>
                              <span className="font-medium text-green-900 break-all text-right">
                                {order.product.razorpayRefundId}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* COD Payment Notice */}
                    {isCOD && !isCancelled && (
                      <div className="bg-yellow-50 p-4 rounded-md border border-yellow-100">
                        <h3 className="font-medium text-yellow-800 mb-2 flex items-center">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Payment Information
                        </h3>
                        <p className="text-sm text-yellow-700">
                          You will pay {formatCurrency(order.totalAmount)} when your order is delivered.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Other Products in Same Order */}
            {orderData.otherProductsInSameOrder && orderData.otherProductsInSameOrder.length > 0 && (
              <div className="p-6 border-t border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Other Products in This Order</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {orderData.otherProductsInSameOrder.map((product) => (
                    <div 
                      key={product.orderProductId}
                      onClick={() => navigate(`/orders/${product.orderProductId}`)}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <img 
                            src={product.images[0]} 
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-md border border-gray-200"
                          />
                        </div>
                        <div className="flex-grow">
                          <h3 className="font-medium text-gray-900">{product.name}</h3>
                          <div className="mt-1 text-sm text-gray-600">
                            <p>Quantity: {product.quantity}</p>
                            <p>Price: {formatCurrency(product.price)}</p>
                            <p>Total: {formatCurrency(product.total)}</p>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            product.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderDetails;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Header from '../Header/Header';

// // Loader Component
// const Loader = ({ fullScreen = false }) => {
//   return (
//     <div className={`flex items-center justify-center ${fullScreen ? 'h-screen w-screen' : 'h-full w-full'}`}>
//       <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//     </div>
//   );
// };

// // Error Message Component
// const ErrorMessage = ({ message, onRetry }) => {
//   return (
//     <div className="max-w-4xl mx-auto p-6">
//       <div className="bg-red-50 border-l-4 border-red-500 p-4">
//         <div className="flex items-center">
//           <div className="flex-shrink-0">
//             <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//             </svg>
//           </div>
//           <div className="ml-3">
//             <p className="text-sm text-red-700">
//               {message}
//               {onRetry && (
//                 <button 
//                   onClick={onRetry}
//                   className="ml-2 text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none"
//                 >
//                   Retry
//                 </button>
//               )}
//             </p>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [orderData, setOrderData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [cancelling, setCancelling] = useState(false);
//   const [showCancelModal, setShowCancelModal] = useState(false);

//   const fetchOrderDetails = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );
//       setOrderData(response.data);
//     } catch (err) {
//       setError(err.response?.data?.message || "Failed to fetch order details");
//       toast.error("Failed to load order details");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     }).format(amount);
//   };

//   const handleCancelOrder = async () => {
//     if (!orderData?.product) return;
    
//     try {
//       setCancelling(true);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.patch(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/cancel-product-by-buyer/${orderData.product.orderId}`,
//         {
//           productId: orderData.product.product.productId,
//           variantId: orderData.product.product.variantId
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );

//       toast.success(response.data.message);
//       // Refresh order details after cancellation
//       await fetchOrderDetails();
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to cancel order");
//     } finally {
//       setCancelling(false);
//       setShowCancelModal(false);
//     }
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { 
//         id: 'ordered', 
//         name: 'Order Placed', 
//         description: 'Your order has been received',
//         apiStatus: 'pending',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7 7v18" />
//           </svg>
//         )
//       },
//       { 
//         id: 'confirmed', 
//         name: 'Order Confirmed', 
//         description: 'Seller has accepted your order',
//         apiStatus: 'confirmed',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       },
//       { 
//         id: 'shipped', 
//         name: 'Shipped', 
//         description: 'Seller has shipped your item',
//         apiStatus: 'shipped',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'in_transit', 
//         name: 'In Transit', 
//         description: 'Your item is on the way',
//         apiStatus: 'in_transit',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'delivered', 
//         name: 'Delivered', 
//         description: 'Your item has arrived',
//         apiStatus: 'delivered',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       }
//     ];

//     let currentStepIndex = 0;
    
//     if (orderData?.product) {
//       if (orderData.product.product.status === 'cancelled') {
//         // If order is cancelled, show all steps as incomplete
//         return steps.map(step => ({
//           ...step,
//           status: 'incomplete',
//           current: false
//         }));
//       }

//       if (orderData.product.product.status === 'confirmed') {
//         currentStepIndex = 1;
//       }

//       if (orderData.product.tracking) {
//         switch (orderData.product.tracking.status) {
//           case 'shipped':
//             currentStepIndex = 2;
//             break;
//           case 'in_transit':
//             currentStepIndex = 3;
//             break;
//           case 'delivered':
//             currentStepIndex = 4;
//             break;
//           case 'not_shipped':
//             currentStepIndex = orderData.product.product.status === 'confirmed' ? 1 : 0;
//             break;
//           default:
//             break;
//         }
//       }
//     }

//     return steps.map((step, index) => ({
//       ...step,
//       status: index <= currentStepIndex ? 'complete' : 'incomplete',
//       current: index === currentStepIndex
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} onRetry={fetchOrderDetails} />;
//   if (!orderData) return <ErrorMessage message="Order not found" />;

//   const order = orderData.product;
//   const trackingSteps = getTrackingSteps();
//   const canCancel = order.tracking?.status !== 'delivered' && 
//                    order.product.status !== 'cancelled' && 
//                    order.product.status !== 'delivered';
//   const isCOD = order.payment.method === 'cod';
//   const showRefundInfo = order.product.status === 'cancelled' && !isCOD;

//   return (
//     <>
//       <Header />
//       <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//         <div className="max-w-6xl mx-auto">
//           {/* Cancel Confirmation Modal */}
//           {showCancelModal && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//               <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
//                 <div className="p-6">
//                   <div className="flex items-center justify-between mb-4">
//                     <h3 className="text-lg font-bold text-gray-900">Confirm Cancellation</h3>
//                     <button 
//                       onClick={() => setShowCancelModal(false)}
//                       className="text-gray-400 hover:text-gray-500"
//                     >
//                       <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                     </button>
//                   </div>
//                   <div className="mb-6">
//                     <div className="flex items-start">
//                       <div className="flex-shrink-0 mt-1">
//                         <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                         </svg>
//                       </div>
//                       <div className="ml-3">
//                         <p className="text-gray-700">
//                           Are you sure you want to cancel this order? 
//                           This action cannot be undone.
//                         </p>
//                         {order.tracking?.status === 'shipped' && (
//                           <p className="text-gray-600 mt-2 text-sm">
//                             Note: Since your order has already been shipped, you may need to return the item to receive a full refund.
//                           </p>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex justify-end space-x-3">
//                     <button
//                       onClick={() => setShowCancelModal(false)}
//                       className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
//                     >
//                       Go Back
//                     </button>
//                     <button
//                       onClick={handleCancelOrder}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
//                     >
//                       {cancelling ? (
//                         <>
//                           <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                           </svg>
//                           Cancelling...
//                         </>
//                       ) : (
//                         'Confirm Cancellation'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           <div className="mb-6">
//             <button 
//               onClick={() => navigate(-1)}
//               className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
//             >
//               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//               </svg>
//               Back to Orders
//             </button>
//           </div>

//           <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//             <div className="p-6 border-b border-gray-200">
//               <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
//                 <div>
//                   <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
//                   <p className="text-gray-500 mt-1">Order ID: {order.orderId}</p>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <span className={`px-3 py-1 rounded-full text-sm font-medium ${
//                     order.product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                     order.product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                     'bg-blue-100 text-blue-800'
//                   }`}>
//                     {order.product.status.charAt(0).toUpperCase() + order.product.status.slice(1)}
//                   </span>
//                   {canCancel && (
//                     <button
//                       onClick={() => setShowCancelModal(true)}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-white border border-red-500 text-red-600 rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center space-x-2"
//                     >
//                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                       <span>Cancel Order</span>
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
            
//             {/* Cancellation Banner if order is cancelled */}
//             {order.product.status === 'cancelled' && (
//               <div className="bg-red-50 border-l-4 border-red-500 p-4">
//                 <div className="flex items-center">
//                   <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                   </svg>
//                   <div>
//                     <strong className="font-medium">This order has been cancelled</strong>
//                     {showRefundInfo ? (
//                       <p className="text-sm mt-1">
//                         {order.product.isRefunded ? (
//                           <>
//                             Your refund of {formatCurrency(order.product.refundedAmount)} was processed on {formatDate(order.product.refundedAt)}.
//                             {order.product.razorpayRefundId && (
//                               <span> Refund ID: {order.product.razorpayRefundId}</span>
//                             )}
//                           </>
//                         ) : (
//                           "Your refund is being processed and will be completed soon."
//                         )}
//                       </p>
//                     ) : (
//                       <p className="text-sm mt-1">
//                         Since you paid via Cash on Delivery, no refund is required.
//                       </p>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             )}
            
//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
//               {/* Product Information */}
//               <div className="lg:col-span-2 space-y-6">
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h2>
                  
//                   <div className="flex flex-col sm:flex-row gap-5">
//                     <div className="flex-shrink-0">
//                       <img 
//                         src={order.product.images[0]} 
//                         alt={order.product.name}
//                         className="w-32 h-32 object-cover rounded-lg border border-gray-200"
//                       />
//                     </div>
                    
//                     <div className="flex-grow">
//                       <h3 className="text-xl font-medium text-gray-900">{order.product.name}</h3>
//                       <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
//                         <div>
//                           <span className="text-gray-500">Quantity:</span>
//                           <span className="ml-2 font-medium text-gray-900">{order.product.quantity}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Price:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatCurrency(order.product.price)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Total:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatCurrency(order.product.total)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Order Date:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatDate(order.orderCreatedAt)}</span>
//                         </div>
//                         {order.product.status === 'cancelled' && (
//                           <>
//                             <div>
//                               <span className="text-gray-500">Cancellation Date:</span>
//                               <span className="ml-2 font-medium text-gray-900">{formatDate(order.product.refundedAt)}</span>
//                             </div>
//                             {showRefundInfo && (
//                               <div>
//                                 <span className="text-gray-500">Refund Amount:</span>
//                                 <span className="ml-2 font-medium text-gray-900">
//                                   {formatCurrency(order.product.refundedAmount)}
//                                 </span>
//                               </div>
//                             )}
//                           </>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Shipping Information */}
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping & Payment</h2>
                  
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div>
//                       <h3 className="font-medium text-gray-900 mb-3">Delivery Address</h3>
//                       <div className="bg-white p-4 rounded-md border border-gray-200">
//                         <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
//                         <p className="text-gray-600 mt-1">{order.shippingAddress.addressLine1}</p>
//                         {order.shippingAddress.addressLine2 && (
//                           <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                         )}
//                         <p className="text-gray-600">
//                           {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                         </p>
//                         <p className="text-gray-600">{order.shippingAddress.country}</p>
//                         <p className="text-gray-600 mt-2">Phone: {order.shippingAddress.phone}</p>
//                       </div>
//                     </div>
                    
//                     <div className="space-y-6">
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Seller Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <p className="text-gray-900">{order.seller.businessName}</p>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Payment Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Method:</span>
//                             <span className="font-medium text-gray-900">{order.payment.method.toUpperCase()}</span>
//                           </div>
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Status:</span>
//                             <span className={`font-medium ${
//                               order.payment.status === 'completed' ? 'text-green-600' : 
//                               order.payment.status === 'failed' ? 'text-red-600' : 
//                               'text-yellow-600'
//                             }`}>
//                               {order.payment.status}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Total Paid:</span>
//                             <span className="font-medium text-gray-900">{formatCurrency(order.totalAmount)}</span>
//                           </div>
//                           {showRefundInfo && order.sellerRefund && (
//                             <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
//                               <h4 className="font-medium text-gray-900">Refund Details</h4>
//                               <div className="flex justify-between">
//                                 <span className="text-gray-600">Refund Status:</span>
//                                 <span className="font-medium text-green-600">
//                                   {order.sellerRefund.isRefunded ? 'Completed' : 'Processing'}
//                                 </span>
//                               </div>
//                               <div className="flex justify-between">
//                                 <span className="text-gray-600">Refund Amount:</span>
//                                 <span className="font-medium text-gray-900">
//                                   {formatCurrency(order.sellerRefund.amount)}
//                                 </span>
//                               </div>
//                               {order.sellerRefund.refundedAt && (
//                                 <div className="flex justify-between">
//                                   <span className="text-gray-600">Refund Date:</span>
//                                   <span className="font-medium text-gray-900">
//                                     {formatDate(order.sellerRefund.refundedAt)}
//                                   </span>
//                                 </div>
//                               )}
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Tracking Progress */}
//               <div className="lg:col-span-1">
//                 <div className="bg-white rounded-lg p-5 border border-gray-200 sticky top-6">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h2>
                  
//                   <div className="space-y-6">
//                     {/* Progress Bar */}
//                     <div className="relative">
//                       {/* Progress line */}
//                       <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      
//                       {trackingSteps.map((step, index) => (
//                         <div key={step.id} className="relative flex items-start pb-8 last:pb-0">
//                           <div className={`absolute left-5 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                             <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
//                           </div>
                          
//                           <div className={`relative flex items-center justify-center w-10 h-10 rounded-full ${step.current ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${
//                             step.status === 'complete' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
//                           }`}>
//                             {step.status === 'complete' ? (
//                               step.icon
//                             ) : (
//                               <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
//                                 {index + 1}
//                               </span>
//                             )}
//                           </div>
                          
//                           <div className="ml-4 pt-1">
//                             <h3 className={`text-sm font-medium ${
//                               step.current ? 'text-blue-600' : 
//                               step.status === 'complete' ? 'text-gray-900' : 'text-gray-500'
//                             }`}>
//                               {step.name}
//                             </h3>
//                             <p className={`text-xs ${
//                               step.current ? 'text-blue-500' : 'text-gray-400'
//                             }`}>
//                               {step.description}
//                             </p>
//                             {step.current && (
//                               <p className="text-xs text-blue-500 mt-1 font-medium">Current Status</p>
//                             )}
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Tracking Details */}
//                     {order.tracking && order.product.status !== 'cancelled' && (
//                       <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
//                         <h3 className="font-medium text-blue-800 mb-2 flex items-center">
//                           <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
//                           </svg>
//                           Tracking Details
//                         </h3>
//                         <div className="space-y-2 text-sm">
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Carrier:</span>
//                             <span className="font-medium text-blue-900">{order.tracking.carrier || 'Not specified'}</span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Status:</span>
//                             <span className="font-medium text-blue-900 capitalize">{order.tracking.status || 'Not available'}</span>
//                           </div>
//                           {order.tracking.awbNumber && (
//                             <div className="flex justify-between">
//                               <span className="text-blue-700">AWB #:</span>
//                               <span className="font-medium text-blue-900">{order.tracking.awbNumber}</span>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}

//                     {/* Refund Summary for Cancelled Orders */}
//                     {showRefundInfo && (
//                       <div className="bg-green-50 p-4 rounded-md border border-green-100">
//                         <h3 className="font-medium text-green-800 mb-2 flex items-center">
//                           <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
//                           </svg>
//                           Refund Summary
//                         </h3>
//                         <div className="space-y-2 text-sm">
//                           <div className="flex justify-between">
//                             <span className="text-green-700">Status:</span>
//                             <span className="font-medium text-green-900">
//                               {order.product.isRefunded ? 'Refund Completed' : 'Refund Processing'}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-green-700">Amount:</span>
//                             <span className="font-medium text-green-900">
//                               {formatCurrency(order.product.refundedAmount)}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-green-700">Processed On:</span>
//                             <span className="font-medium text-green-900">
//                               {formatDate(order.product.refundedAt)}
//                             </span>
//                           </div>
//                           {order.product.razorpayRefundId && (
//                             <div className="flex justify-between">
//                               <span className="text-green-700">Refund ID:</span>
//                               <span className="font-medium text-green-900 break-all text-right">
//                                 {order.product.razorpayRefundId}
//                               </span>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Other Products in Same Order */}
//             {orderData.otherProductsInSameOrder && orderData.otherProductsInSameOrder.length > 0 && (
//               <div className="p-6 border-t border-gray-200">
//                 <h2 className="text-lg font-semibold text-gray-900 mb-4">Other Products in This Order</h2>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   {orderData.otherProductsInSameOrder.map((product) => (
//                     <div 
//                       key={product.orderProductId}
//                       onClick={() => navigate(`/orders/${product.orderProductId}`)}
//                       className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
//                     >
//                       <div className="flex items-start gap-4">
//                         <div className="flex-shrink-0">
//                           <img 
//                             src={product.images[0]} 
//                             alt={product.name}
//                             className="w-16 h-16 object-cover rounded-md border border-gray-200"
//                           />
//                         </div>
//                         <div className="flex-grow">
//                           <h3 className="font-medium text-gray-900">{product.name}</h3>
//                           <div className="mt-1 text-sm text-gray-600">
//                             <p>Quantity: {product.quantity}</p>
//                             <p>Price: {formatCurrency(product.price)}</p>
//                             <p>Total: {formatCurrency(product.total)}</p>
//                           </div>
//                         </div>
//                         <div className="flex-shrink-0">
//                           <span className={`px-2 py-1 rounded-full text-xs font-medium ${
//                             product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                             product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                             'bg-blue-100 text-blue-800'
//                           }`}>
//                             {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default OrderDetails;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Header from '../Header/Header';

// // Loader Component
// const Loader = ({ fullScreen = false }) => {
//   return (
//     <div className={`flex items-center justify-center ${fullScreen ? 'h-screen w-screen' : 'h-full w-full'}`}>
//       <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//     </div>
//   );
// };

// // Error Message Component
// const ErrorMessage = ({ message, onRetry }) => {
//   return (
//     <div className="max-w-4xl mx-auto p-6">
//       <div className="bg-red-50 border-l-4 border-red-500 p-4">
//         <div className="flex items-center">
//           <div className="flex-shrink-0">
//             <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//             </svg>
//           </div>
//           <div className="ml-3">
//             <p className="text-sm text-red-700">
//               {message}
//               {onRetry && (
//                 <button 
//                   onClick={onRetry}
//                   className="ml-2 text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none"
//                 >
//                   Retry
//                 </button>
//               )}
//             </p>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [orderData, setOrderData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [cancelling, setCancelling] = useState(false);
//   const [showCancelModal, setShowCancelModal] = useState(false);

//   const fetchOrderDetails = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );
//       setOrderData(response.data);
//     } catch (err) {
//       setError(err.response?.data?.message || "Failed to fetch order details");
//       toast.error("Failed to load order details");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat('en-IN', {
//       style: 'currency',
//       currency: 'INR',
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2
//     }).format(amount);
//   };

//   const handleCancelOrder = async () => {
//     if (!orderData?.product) return;
    
//     try {
//       setCancelling(true);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.patch(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/cancel-product-by-buyer/${orderData.product.orderId}`,
//         {
//           productId: orderData.product.product.productId,
//           variantId: orderData.product.product.variantId
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );

//       toast.success(response.data.message);
//       // Refresh order details after cancellation
//       await fetchOrderDetails();
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to cancel order");
//     } finally {
//       setCancelling(false);
//       setShowCancelModal(false);
//     }
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { 
//         id: 'ordered', 
//         name: 'Order Placed', 
//         description: 'Your order has been received',
//         apiStatus: 'pending',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7 7v18" />
//           </svg>
//         )
//       },
//       { 
//         id: 'confirmed', 
//         name: 'Order Confirmed', 
//         description: 'Seller has accepted your order',
//         apiStatus: 'confirmed',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       },
//       { 
//         id: 'shipped', 
//         name: 'Shipped', 
//         description: 'Seller has shipped your item',
//         apiStatus: 'shipped',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'in_transit', 
//         name: 'In Transit', 
//         description: 'Your item is on the way',
//         apiStatus: 'in_transit',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'delivered', 
//         name: 'Delivered', 
//         description: 'Your item has arrived',
//         apiStatus: 'delivered',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       }
//     ];

//     let currentStepIndex = 0;
    
//     if (orderData?.product) {
//       if (orderData.product.product.status === 'cancelled') {
//         // If order is cancelled, show all steps as incomplete
//         return steps.map(step => ({
//           ...step,
//           status: 'incomplete',
//           current: false
//         }));
//       }

//       if (orderData.product.product.status === 'confirmed') {
//         currentStepIndex = 1;
//       }

//       if (orderData.product.tracking) {
//         switch (orderData.product.tracking.status) {
//           case 'shipped':
//             currentStepIndex = 2;
//             break;
//           case 'in_transit':
//             currentStepIndex = 3;
//             break;
//           case 'delivered':
//             currentStepIndex = 4;
//             break;
//           case 'not_shipped':
//             currentStepIndex = orderData.product.product.status === 'confirmed' ? 1 : 0;
//             break;
//           default:
//             break;
//         }
//       }
//     }

//     return steps.map((step, index) => ({
//       ...step,
//       status: index <= currentStepIndex ? 'complete' : 'incomplete',
//       current: index === currentStepIndex
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} onRetry={fetchOrderDetails} />;
//   if (!orderData) return <ErrorMessage message="Order not found" />;

//   const order = orderData.product;
//   const trackingSteps = getTrackingSteps();
//   const canCancel = order.tracking?.status !== 'delivered' && 
//                    order.product.status !== 'cancelled' && 
//                    order.product.status !== 'delivered';

//   return (
//     <>
//       <Header />
//       <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//         <div className="max-w-6xl mx-auto">
//           {/* Cancel Confirmation Modal */}
//           {showCancelModal && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//               <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
//                 <div className="p-6">
//                   <div className="flex items-center justify-between mb-4">
//                     <h3 className="text-lg font-bold text-gray-900">Confirm Cancellation</h3>
//                     <button 
//                       onClick={() => setShowCancelModal(false)}
//                       className="text-gray-400 hover:text-gray-500"
//                     >
//                       <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                     </button>
//                   </div>
//                   <div className="mb-6">
//                     <div className="flex items-start">
//                       <div className="flex-shrink-0 mt-1">
//                         <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                         </svg>
//                       </div>
//                       <div className="ml-3">
//                         <p className="text-gray-700">
//                           Are you sure you want to cancel this order? 
//                           This action cannot be undone.
//                         </p>
//                         {order.tracking?.status === 'shipped' && (
//                           <p className="text-gray-600 mt-2 text-sm">
//                             Note: Since your order has already been shipped, you may need to return the item to receive a full refund.
//                           </p>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex justify-end space-x-3">
//                     <button
//                       onClick={() => setShowCancelModal(false)}
//                       className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
//                     >
//                       Go Back
//                     </button>
//                     <button
//                       onClick={handleCancelOrder}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
//                     >
//                       {cancelling ? (
//                         <>
//                           <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                           </svg>
//                           Cancelling...
//                         </>
//                       ) : (
//                         'Confirm Cancellation'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           <div className="mb-6">
//             <button 
//               onClick={() => navigate(-1)}
//               className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
//             >
//               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//               </svg>
//               Back to Orders
//             </button>
//           </div>

//           <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//             <div className="p-6 border-b border-gray-200">
//               <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
//                 <div>
//                   <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
//                   <p className="text-gray-500 mt-1">Order ID: {order.orderId}</p>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <span className={`px-3 py-1 rounded-full text-sm font-medium ${
//                     order.product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                     order.product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                     'bg-blue-100 text-blue-800'
//                   }`}>
//                     {order.product.status.charAt(0).toUpperCase() + order.product.status.slice(1)}
//                   </span>
//                   {canCancel && (
//                     <button
//                       onClick={() => setShowCancelModal(true)}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-white border border-red-500 text-red-600 rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center space-x-2"
//                     >
//                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                       <span>Cancel Order</span>
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
            
//             {/* Cancellation Banner if order is cancelled */}
//             {order.product.status === 'cancelled' && (
//               <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4">
//                 <div className="flex items-center">
//                   <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                   </svg>
//                   <div>
//                     <strong className="font-medium">This order has been cancelled</strong>
//                     <p className="text-sm mt-1">
//                       {order.product.isRefunded ? (
//                         <>
//                           Your refund of {formatCurrency(order.product.refundedAmount)} was processed on {formatDate(order.product.refundedAt)}.
//                           {order.product.razorpayRefundId && (
//                             <span> Refund ID: {order.product.razorpayRefundId}</span>
//                           )}
//                         </>
//                       ) : (
//                         "Your refund is being processed and will be completed soon."
//                       )}
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             )}
            
//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
//               {/* Product Information */}
//               <div className="lg:col-span-2 space-y-6">
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h2>
                  
//                   <div className="flex flex-col sm:flex-row gap-5">
//                     <div className="flex-shrink-0">
//                       <img 
//                         src={order.product.images[0]} 
//                         alt={order.product.name}
//                         className="w-32 h-32 object-cover rounded-lg border border-gray-200"
//                       />
//                     </div>
                    
//                     <div className="flex-grow">
//                       <h3 className="text-xl font-medium text-gray-900">{order.product.name}</h3>
//                       <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
//                         <div>
//                           <span className="text-gray-500">Quantity:</span>
//                           <span className="ml-2 font-medium text-gray-900">{order.product.quantity}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Price:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatCurrency(order.product.price)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Total:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatCurrency(order.product.total)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Order Date:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatDate(order.orderCreatedAt)}</span>
//                         </div>
//                         {order.product.status === 'cancelled' && (
//                           <>
//                             <div>
//                               <span className="text-gray-500">Cancellation Date:</span>
//                               <span className="ml-2 font-medium text-gray-900">{formatDate(order.product.refundedAt)}</span>
//                             </div>
//                             <div>
//                               <span className="text-gray-500">Refund Amount:</span>
//                               <span className="ml-2 font-medium text-gray-900">
//                                 {formatCurrency(order.product.refundedAmount)}
//                               </span>
//                             </div>
//                           </>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Shipping Information */}
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping & Payment</h2>
                  
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div>
//                       <h3 className="font-medium text-gray-900 mb-3">Delivery Address</h3>
//                       <div className="bg-white p-4 rounded-md border border-gray-200">
//                         <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
//                         <p className="text-gray-600 mt-1">{order.shippingAddress.addressLine1}</p>
//                         {order.shippingAddress.addressLine2 && (
//                           <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                         )}
//                         <p className="text-gray-600">
//                           {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                         </p>
//                         <p className="text-gray-600">{order.shippingAddress.country}</p>
//                         <p className="text-gray-600 mt-2">Phone: {order.shippingAddress.phone}</p>
//                       </div>
//                     </div>
                    
//                     <div className="space-y-6">
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Seller Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <p className="text-gray-900">{order.seller.businessName}</p>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Payment Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Method:</span>
//                             <span className="font-medium text-gray-900">{order.payment.method.toUpperCase()}</span>
//                           </div>
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Status:</span>
//                             <span className={`font-medium ${
//                               order.payment.status === 'completed' ? 'text-green-600' : 
//                               order.payment.status === 'failed' ? 'text-red-600' : 
//                               'text-yellow-600'
//                             }`}>
//                               {order.payment.status}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Total Paid:</span>
//                             <span className="font-medium text-gray-900">{formatCurrency(order.totalAmount)}</span>
//                           </div>
//                           {order.product.status === 'cancelled' && order.sellerRefund && (
//                             <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
//                               <h4 className="font-medium text-gray-900">Refund Details</h4>
//                               <div className="flex justify-between">
//                                 <span className="text-gray-600">Refund Status:</span>
//                                 <span className="font-medium text-green-600">
//                                   {order.sellerRefund.isRefunded ? 'Completed' : 'Processing'}
//                                 </span>
//                               </div>
//                               <div className="flex justify-between">
//                                 <span className="text-gray-600">Refund Amount:</span>
//                                 <span className="font-medium text-gray-900">
//                                   {formatCurrency(order.sellerRefund.amount)}
//                                 </span>
//                               </div>
//                               {order.sellerRefund.refundedAt && (
//                                 <div className="flex justify-between">
//                                   <span className="text-gray-600">Refund Date:</span>
//                                   <span className="font-medium text-gray-900">
//                                     {formatDate(order.sellerRefund.refundedAt)}
//                                   </span>
//                                 </div>
//                               )}
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Tracking Progress */}
//               <div className="lg:col-span-1">
//                 <div className="bg-white rounded-lg p-5 border border-gray-200 sticky top-6">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h2>
                  
//                   <div className="space-y-6">
//                     {/* Progress Bar */}
//                     <div className="relative">
//                       {/* Progress line */}
//                       <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      
//                       {trackingSteps.map((step, index) => (
//                         <div key={step.id} className="relative flex items-start pb-8 last:pb-0">
//                           <div className={`absolute left-5 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                             <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
//                           </div>
                          
//                           <div className={`relative flex items-center justify-center w-10 h-10 rounded-full ${step.current ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${
//                             step.status === 'complete' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
//                           }`}>
//                             {step.status === 'complete' ? (
//                               step.icon
//                             ) : (
//                               <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
//                                 {index + 1}
//                               </span>
//                             )}
//                           </div>
                          
//                           <div className="ml-4 pt-1">
//                             <h3 className={`text-sm font-medium ${
//                               step.current ? 'text-blue-600' : 
//                               step.status === 'complete' ? 'text-gray-900' : 'text-gray-500'
//                             }`}>
//                               {step.name}
//                             </h3>
//                             <p className={`text-xs ${
//                               step.current ? 'text-blue-500' : 'text-gray-400'
//                             }`}>
//                               {step.description}
//                             </p>
//                             {step.current && (
//                               <p className="text-xs text-blue-500 mt-1 font-medium">Current Status</p>
//                             )}
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Tracking Details */}
//                     {order.tracking && order.product.status !== 'cancelled' && (
//                       <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
//                         <h3 className="font-medium text-blue-800 mb-2 flex items-center">
//                           <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
//                           </svg>
//                           Tracking Details
//                         </h3>
//                         <div className="space-y-2 text-sm">
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Carrier:</span>
//                             <span className="font-medium text-blue-900">{order.tracking.carrier || 'Not specified'}</span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Status:</span>
//                             <span className="font-medium text-blue-900 capitalize">{order.tracking.status || 'Not available'}</span>
//                           </div>
//                           {order.tracking.awbNumber && (
//                             <div className="flex justify-between">
//                               <span className="text-blue-700">AWB #:</span>
//                               <span className="font-medium text-blue-900">{order.tracking.awbNumber}</span>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}

//                     {/* Refund Summary for Cancelled Orders */}
//                     {order.product.status === 'cancelled' && (
//                       <div className="bg-green-50 p-4 rounded-md border border-green-100">
//                         <h3 className="font-medium text-green-800 mb-2 flex items-center">
//                           <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
//                           </svg>
//                           Refund Summary
//                         </h3>
//                         <div className="space-y-2 text-sm">
//                           <div className="flex justify-between">
//                             <span className="text-green-700">Status:</span>
//                             <span className="font-medium text-green-900">
//                               {order.product.isRefunded ? 'Refund Completed' : 'Refund Processing'}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-green-700">Amount:</span>
//                             <span className="font-medium text-green-900">
//                               {formatCurrency(order.product.refundedAmount)}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-green-700">Processed On:</span>
//                             <span className="font-medium text-green-900">
//                               {formatDate(order.product.refundedAt)}
//                             </span>
//                           </div>
//                           {order.product.razorpayRefundId && (
//                             <div className="flex justify-between">
//                               <span className="text-green-700">Refund ID:</span>
//                               <span className="font-medium text-green-900 break-all text-right">
//                                 {order.product.razorpayRefundId}
//                               </span>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Other Products in Same Order */}
//             {orderData.otherProductsInSameOrder && orderData.otherProductsInSameOrder.length > 0 && (
//               <div className="p-6 border-t border-gray-200">
//                 <h2 className="text-lg font-semibold text-gray-900 mb-4">Other Products in This Order</h2>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   {orderData.otherProductsInSameOrder.map((product) => (
//                     <div 
//                       key={product.orderProductId}
//                       onClick={() => navigate(`/orders/${product.orderProductId}`)}
//                       className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
//                     >
//                       <div className="flex items-start gap-4">
//                         <div className="flex-shrink-0">
//                           <img 
//                             src={product.images[0]} 
//                             alt={product.name}
//                             className="w-16 h-16 object-cover rounded-md border border-gray-200"
//                           />
//                         </div>
//                         <div className="flex-grow">
//                           <h3 className="font-medium text-gray-900">{product.name}</h3>
//                           <div className="mt-1 text-sm text-gray-600">
//                             <p>Quantity: {product.quantity}</p>
//                             <p>Price: {formatCurrency(product.price)}</p>
//                             <p>Total: {formatCurrency(product.total)}</p>
//                           </div>
//                         </div>
//                         <div className="flex-shrink-0">
//                           <span className={`px-2 py-1 rounded-full text-xs font-medium ${
//                             product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                             product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                             'bg-blue-100 text-blue-800'
//                           }`}>
//                             {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default OrderDetails;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Header from '../Header/Header';

// // Loader Component
// const Loader = ({ fullScreen = false }) => {
//   return (
//     <div className={`flex items-center justify-center ${fullScreen ? 'h-screen w-screen' : 'h-full w-full'}`}>
//       <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
//     </div>
//   );
// };

// // Error Message Component
// const ErrorMessage = ({ message, onRetry }) => {
//   return (
//     <div className="max-w-4xl mx-auto p-6">
//       <div className="bg-red-50 border-l-4 border-red-500 p-4">
//         <div className="flex items-center">
//           <div className="flex-shrink-0">
//             <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//             </svg>
//           </div>
//           <div className="ml-3">
//             <p className="text-sm text-red-700">
//               {message}
//               {onRetry && (
//                 <button 
//                   onClick={onRetry}
//                   className="ml-2 text-sm font-medium text-red-600 hover:text-red-500 focus:outline-none"
//                 >
//                   Retry
//                 </button>
//               )}
//             </p>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [orderData, setOrderData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [cancelling, setCancelling] = useState(false);
//   const [showCancelModal, setShowCancelModal] = useState(false);

//   const fetchOrderDetails = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );
//       setOrderData(response.data);
//     } catch (err) {
//       setError(err.response?.data?.message || "Failed to fetch order details");
//       toast.error("Failed to load order details");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const handleCancelOrder = async () => {
//     if (!orderData?.product) return;
    
//     try {
//       setCancelling(true);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.patch(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/cancel-product-by-buyer/${orderData.product.orderId}`,
//         {
//           productId: orderData.product.product.productId,
//           variantId: orderData.product.product.variantId
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );

//       toast.success(response.data.message);
//       // Refresh order details after cancellation
//       await fetchOrderDetails();
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to cancel order");
//     } finally {
//       setCancelling(false);
//       setShowCancelModal(false);
//     }
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { 
//         id: 'ordered', 
//         name: 'Order Placed', 
//         description: 'Your order has been received',
//         apiStatus: 'pending',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7 7v18" />
//           </svg>
//         )
//       },
//       { 
//         id: 'confirmed', 
//         name: 'Order Confirmed', 
//         description: 'Seller has accepted your order',
//         apiStatus: 'confirmed',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       },
//       { 
//         id: 'shipped', 
//         name: 'Shipped', 
//         description: 'Seller has shipped your item',
//         apiStatus: 'shipped',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'in_transit', 
//         name: 'In Transit', 
//         description: 'Your item is on the way',
//         apiStatus: 'in_transit',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'delivered', 
//         name: 'Delivered', 
//         description: 'Your item has arrived',
//         apiStatus: 'delivered',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       }
//     ];

//     let currentStepIndex = 0;
    
//     if (orderData?.product) {
//       if (orderData.product.product.status === 'cancelled') {
//         // If order is cancelled, show all steps as incomplete
//         return steps.map(step => ({
//           ...step,
//           status: 'incomplete',
//           current: false
//         }));
//       }

//       if (orderData.product.product.status === 'confirmed') {
//         currentStepIndex = 1;
//       }

//       if (orderData.product.tracking) {
//         switch (orderData.product.tracking.status) {
//           case 'shipped':
//             currentStepIndex = 2;
//             break;
//           case 'in_transit':
//             currentStepIndex = 3;
//             break;
//           case 'delivered':
//             currentStepIndex = 4;
//             break;
//           case 'not_shipped':
//             currentStepIndex = orderData.product.product.status === 'confirmed' ? 1 : 0;
//             break;
//           default:
//             break;
//         }
//       }
//     }

//     return steps.map((step, index) => ({
//       ...step,
//       status: index <= currentStepIndex ? 'complete' : 'incomplete',
//       current: index === currentStepIndex
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} onRetry={fetchOrderDetails} />;
//   if (!orderData) return <ErrorMessage message="Order not found" />;

//   const order = orderData.product;
//   const trackingSteps = getTrackingSteps();
//   const canCancel = order.tracking?.status !== 'delivered' && 
//                    order.product.status !== 'cancelled' && 
//                    order.product.status !== 'delivered';

//   return (
//     <>
//       <Header />
//       <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//         <div className="max-w-6xl mx-auto">
//           {/* Cancel Confirmation Modal */}
//           {showCancelModal && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//               <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
//                 <div className="p-6">
//                   <div className="flex items-center justify-between mb-4">
//                     <h3 className="text-lg font-bold text-gray-900">Confirm Cancellation</h3>
//                     <button 
//                       onClick={() => setShowCancelModal(false)}
//                       className="text-gray-400 hover:text-gray-500"
//                     >
//                       <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                     </button>
//                   </div>
//                   <div className="mb-6">
//                     <div className="flex items-start">
//                       <div className="flex-shrink-0 mt-1">
//                         <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                         </svg>
//                       </div>
//                       <div className="ml-3">
//                         <p className="text-gray-700">
//                           Are you sure you want to cancel this order? 
//                           This action cannot be undone.
//                         </p>
//                         {order.tracking?.status === 'shipped' && (
//                           <p className="text-gray-600 mt-2 text-sm">
//                             Note: Since your order has already been shipped, you may need to return the item to receive a full refund.
//                           </p>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex justify-end space-x-3">
//                     <button
//                       onClick={() => setShowCancelModal(false)}
//                       className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
//                     >
//                       Go Back
//                     </button>
//                     <button
//                       onClick={handleCancelOrder}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
//                     >
//                       {cancelling ? (
//                         <>
//                           <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                           </svg>
//                           Cancelling...
//                         </>
//                       ) : (
//                         'Confirm Cancellation'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           <div className="mb-6">
//             <button 
//               onClick={() => navigate(-1)}
//               className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
//             >
//               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//               </svg>
//               Back to Orders
//             </button>
//           </div>

//           <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//             <div className="p-6 border-b border-gray-200">
//               <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
//                 <div>
//                   <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
//                   <p className="text-gray-500 mt-1">Order ID: {order.orderId}</p>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <span className={`px-3 py-1 rounded-full text-sm font-medium ${
//                     order.product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                     order.product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                     'bg-blue-100 text-blue-800'
//                   }`}>
//                     {order.product.status.charAt(0).toUpperCase() + order.product.status.slice(1)}
//                   </span>
//                   {canCancel && (
//                     <button
//                       onClick={() => setShowCancelModal(true)}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-white border border-red-500 text-red-600 rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center space-x-2"
//                     >
//                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                       <span>Cancel Order</span>
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
            
//             {/* Cancellation Banner if order is cancelled */}
//             {order.product.status === 'cancelled' && (
//               <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4">
//                 <div className="flex items-center">
//                   <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                   </svg>
//                   <div>
//                     <strong className="font-medium">This order has been cancelled</strong>
//                     <p className="text-sm mt-1">Your refund will be processed according to the seller's policy.</p>
//                   </div>
//                 </div>
//               </div>
//             )}
            
//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
//               {/* Product Information */}
//               <div className="lg:col-span-2 space-y-6">
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h2>
                  
//                   <div className="flex flex-col sm:flex-row gap-5">
//                     <div className="flex-shrink-0">
//                       <img 
//                         src={order.product.images[0]} 
//                         alt={order.product.name}
//                         className="w-32 h-32 object-cover rounded-lg border border-gray-200"
//                       />
//                     </div>
                    
//                     <div className="flex-grow">
//                       <h3 className="text-xl font-medium text-gray-900">{order.product.name}</h3>
//                       <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
//                         <div>
//                           <span className="text-gray-500">Quantity:</span>
//                           <span className="ml-2 font-medium text-gray-900">{order.product.quantity}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Price:</span>
//                           <span className="ml-2 font-medium text-gray-900">₹{order.product.price.toFixed(2)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Total:</span>
//                           <span className="ml-2 font-medium text-gray-900">₹{order.product.total.toFixed(2)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Order Date:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatDate(order.orderCreatedAt)}</span>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Shipping Information */}
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping & Payment</h2>
                  
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div>
//                       <h3 className="font-medium text-gray-900 mb-3">Delivery Address</h3>
//                       <div className="bg-white p-4 rounded-md border border-gray-200">
//                         <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
//                         <p className="text-gray-600 mt-1">{order.shippingAddress.addressLine1}</p>
//                         {order.shippingAddress.addressLine2 && (
//                           <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                         )}
//                         <p className="text-gray-600">
//                           {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                         </p>
//                         <p className="text-gray-600">{order.shippingAddress.country}</p>
//                         <p className="text-gray-600 mt-2">Phone: {order.shippingAddress.phone}</p>
//                       </div>
//                     </div>
                    
//                     <div className="space-y-6">
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Seller Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <p className="text-gray-900">{order.seller.businessName}</p>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Payment Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Method:</span>
//                             <span className="font-medium text-gray-900">{order.payment.method.toUpperCase()}</span>
//                           </div>
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Status:</span>
//                             <span className={`font-medium ${
//                               order.payment.status === 'completed' ? 'text-green-600' : 
//                               order.payment.status === 'failed' ? 'text-red-600' : 
//                               'text-yellow-600'
//                             }`}>
//                               {order.payment.status}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Total Paid:</span>
//                             <span className="font-medium text-gray-900">₹{order.totalAmount.toFixed(2)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Tracking Progress */}
//               <div className="lg:col-span-1">
//                 <div className="bg-white rounded-lg p-5 border border-gray-200 sticky top-6">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h2>
                  
//                   <div className="space-y-6">
//                     {/* Progress Bar */}
//                     <div className="relative">
//                       {/* Progress line */}
//                       <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      
//                       {trackingSteps.map((step, index) => (
//                         <div key={step.id} className="relative flex items-start pb-8 last:pb-0">
//                           <div className={`absolute left-5 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                             <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
//                           </div>
                          
//                           <div className={`relative flex items-center justify-center w-10 h-10 rounded-full ${step.current ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${
//                             step.status === 'complete' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
//                           }`}>
//                             {step.status === 'complete' ? (
//                               step.icon
//                             ) : (
//                               <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
//                                 {index + 1}
//                               </span>
//                             )}
//                           </div>
                          
//                           <div className="ml-4 pt-1">
//                             <h3 className={`text-sm font-medium ${
//                               step.current ? 'text-blue-600' : 
//                               step.status === 'complete' ? 'text-gray-900' : 'text-gray-500'
//                             }`}>
//                               {step.name}
//                             </h3>
//                             <p className={`text-xs ${
//                               step.current ? 'text-blue-500' : 'text-gray-400'
//                             }`}>
//                               {step.description}
//                             </p>
//                             {step.current && (
//                               <p className="text-xs text-blue-500 mt-1 font-medium">Current Status</p>
//                             )}
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Tracking Details */}
//                     {order.tracking && order.product.status !== 'cancelled' && (
//                       <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
//                         <h3 className="font-medium text-blue-800 mb-2 flex items-center">
//                           <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
//                           </svg>
//                           Tracking Details
//                         </h3>
//                         <div className="space-y-2 text-sm">
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Carrier:</span>
//                             <span className="font-medium text-blue-900">{order.tracking.carrier || 'Not specified'}</span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Status:</span>
//                             <span className="font-medium text-blue-900 capitalize">{order.tracking.status || 'Not available'}</span>
//                           </div>
//                           {order.tracking.awbNumber && (
//                             <div className="flex justify-between">
//                               <span className="text-blue-700">AWB #:</span>
//                               <span className="font-medium text-blue-900">{order.tracking.awbNumber}</span>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Other Products in Same Order */}
//             {orderData.otherProductsInSameOrder && orderData.otherProductsInSameOrder.length > 0 && (
//               <div className="p-6 border-t border-gray-200">
//                 <h2 className="text-lg font-semibold text-gray-900 mb-4">Other Products in This Order</h2>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   {orderData.otherProductsInSameOrder.map((product) => (
//                     <div 
//                       key={product.orderProductId}
//                       onClick={() => navigate(`/orders/${product.orderProductId}`)}
//                       className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
//                     >
//                       <div className="flex items-start gap-4">
//                         <div className="flex-shrink-0">
//                           <img 
//                             src={product.images[0]} 
//                             alt={product.name}
//                             className="w-16 h-16 object-cover rounded-md border border-gray-200"
//                           />
//                         </div>
//                         <div className="flex-grow">
//                           <h3 className="font-medium text-gray-900">{product.name}</h3>
//                           <div className="mt-1 text-sm text-gray-600">
//                             <p>Quantity: {product.quantity}</p>
//                             <p>Price: ₹{product.price.toFixed(2)}</p>
//                             <p>Total: ₹{product.total.toFixed(2)}</p>
//                           </div>
//                         </div>
//                         <div className="flex-shrink-0">
//                           <span className={`px-2 py-1 rounded-full text-xs font-medium ${
//                             product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                             product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                             'bg-blue-100 text-blue-800'
//                           }`}>
//                             {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default OrderDetails;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Loader from './Loader';
// import ErrorMessage from './ErrorMessage';
// import Header from '../Header/Header';

// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [orderData, setOrderData] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [cancelling, setCancelling] = useState(false);
//   const [showCancelModal, setShowCancelModal] = useState(false);

//   useEffect(() => {
//     const fetchOrderDetails = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
//           {
//             headers: { Authorization: `Bearer ${token}` }
//           }
//         );
//         setOrderData(response.data);
//       } catch (err) {
//         setError(err.response?.data?.message || "Failed to fetch order details");
//         toast.error("Failed to load order details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const handleCancelOrder = async () => {
//     if (!orderData?.product) return;
    
//     try {
//       setCancelling(true);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.patch(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/cancel-product-by-buyer/${orderData.product.orderId}`,
//         {
//           productId: orderData.product.product.productId,
//           variantId: orderData.product.product.variantId
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );

//       toast.success(response.data.message);
//       // Refresh order details after cancellation
//       const updatedResponse = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );
//       setOrderData(updatedResponse.data);
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to cancel order");
//     } finally {
//       setCancelling(false);
//       setShowCancelModal(false);
//     }
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { 
//         id: 'ordered', 
//         name: 'Order Placed', 
//         description: 'Your order has been received',
//         apiStatus: 'pending',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7 7v18" />
//           </svg>
//         )
//       },
//       { 
//         id: 'confirmed', 
//         name: 'Order Confirmed', 
//         description: 'Seller has accepted your order',
//         apiStatus: 'confirmed',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       },
//       { 
//         id: 'shipped', 
//         name: 'Shipped', 
//         description: 'Seller has shipped your item',
//         apiStatus: 'shipped',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'in_transit', 
//         name: 'In Transit', 
//         description: 'Your item is on the way',
//         apiStatus: 'in_transit',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'delivered', 
//         name: 'Delivered', 
//         description: 'Your item has arrived',
//         apiStatus: 'delivered',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       }
//     ];

//     let currentStepIndex = 0;
    
//     if (orderData?.product) {
//       if (orderData.product.product.status === 'cancelled') {
//         // If order is cancelled, show all steps as incomplete
//         return steps.map(step => ({
//           ...step,
//           status: 'incomplete',
//           current: false
//         }));
//       }

//       if (orderData.product.product.status === 'confirmed') {
//         currentStepIndex = 1;
//       }

//       if (orderData.product.tracking) {
//         switch (orderData.product.tracking.status) {
//           case 'shipped':
//             currentStepIndex = 2;
//             break;
//           case 'in_transit':
//             currentStepIndex = 3;
//             break;
//           case 'delivered':
//             currentStepIndex = 4;
//             break;
//           case 'not_shipped':
//             currentStepIndex = orderData.product.product.status === 'confirmed' ? 1 : 0;
//             break;
//           default:
//             break;
//         }
//       }
//     }

//     return steps.map((step, index) => ({
//       ...step,
//       status: index <= currentStepIndex ? 'complete' : 'incomplete',
//       current: index === currentStepIndex
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} />;
//   if (!orderData) return <ErrorMessage message="Order not found" />;

//   const order = orderData.product;
//   const trackingSteps = getTrackingSteps();
//   const canCancel = order.tracking?.status !== 'delivered' && 
//                    order.product.status !== 'cancelled' && 
//                    order.product.status !== 'delivered';

//   return (
//     <>
//       <Header />
//       <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//         <div className="max-w-6xl mx-auto">
//           {/* Cancel Confirmation Modal */}
//           {showCancelModal && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//               <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
//                 <div className="p-6">
//                   <div className="flex items-center justify-between mb-4">
//                     <h3 className="text-lg font-bold text-gray-900">Confirm Cancellation</h3>
//                     <button 
//                       onClick={() => setShowCancelModal(false)}
//                       className="text-gray-400 hover:text-gray-500"
//                     >
//                       <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                     </button>
//                   </div>
//                   <div className="mb-6">
//                     <div className="flex items-start">
//                       <div className="flex-shrink-0 mt-1">
//                         <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                         </svg>
//                       </div>
//                       <div className="ml-3">
//                         <p className="text-gray-700">
//                           Are you sure you want to cancel this order? 
//                           This action cannot be undone.
//                         </p>
//                         {order.tracking?.status === 'shipped' && (
//                           <p className="text-gray-600 mt-2 text-sm">
//                             Note: Since your order has already been shipped, you may need to return the item to receive a full refund.
//                           </p>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex justify-end space-x-3">
//                     <button
//                       onClick={() => setShowCancelModal(false)}
//                       className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
//                     >
//                       Go Back
//                     </button>
//                     <button
//                       onClick={handleCancelOrder}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
//                     >
//                       {cancelling ? (
//                         <>
//                           <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                           </svg>
//                           Cancelling...
//                         </>
//                       ) : (
//                         'Confirm Cancellation'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           <div className="mb-6">
//             <button 
//               onClick={() => navigate(-1)}
//               className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
//             >
//               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//               </svg>
//               Back to Orders
//             </button>
//           </div>

//           <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//             <div className="p-6 border-b border-gray-200">
//               <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
//                 <div>
//                   <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
//                   <p className="text-gray-500 mt-1">Order ID: {order.orderId}</p>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <span className={`px-3 py-1 rounded-full text-sm font-medium ${
//                     order.product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                     order.product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                     'bg-blue-100 text-blue-800'
//                   }`}>
//                     {order.product.status.charAt(0).toUpperCase() + order.product.status.slice(1)}
//                   </span>
//                   {canCancel && (
//                     <button
//                       onClick={() => setShowCancelModal(true)}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-white border border-red-500 text-red-600 rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center space-x-2"
//                     >
//                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                       <span>Cancel Order</span>
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
            
//             {/* Cancellation Banner if order is cancelled */}
//             {order.product.status === 'cancelled' && (
//               <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4">
//                 <div className="flex items-center">
//                   <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                   </svg>
//                   <div>
//                     <strong className="font-medium">This order has been cancelled</strong>
//                     <p className="text-sm mt-1">Your refund will be processed according to the seller's policy.</p>
//                   </div>
//                 </div>
//               </div>
//             )}
            
//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
//               {/* Product Information */}
//               <div className="lg:col-span-2 space-y-6">
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h2>
                  
//                   <div className="flex flex-col sm:flex-row gap-5">
//                     <div className="flex-shrink-0">
//                       <img 
//                         src={order.product.images[0]} 
//                         alt={order.product.name}
//                         className="w-32 h-32 object-cover rounded-lg border border-gray-200"
//                       />
//                     </div>
                    
//                     <div className="flex-grow">
//                       <h3 className="text-xl font-medium text-gray-900">{order.product.name}</h3>
//                       <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
//                         <div>
//                           <span className="text-gray-500">Quantity:</span>
//                           <span className="ml-2 font-medium text-gray-900">{order.product.quantity}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Price:</span>
//                           <span className="ml-2 font-medium text-gray-900">₹{order.product.price.toFixed(2)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Total:</span>
//                           <span className="ml-2 font-medium text-gray-900">₹{order.product.total.toFixed(2)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Order Date:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatDate(order.orderCreatedAt)}</span>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Shipping Information */}
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping & Payment</h2>
                  
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div>
//                       <h3 className="font-medium text-gray-900 mb-3">Delivery Address</h3>
//                       <div className="bg-white p-4 rounded-md border border-gray-200">
//                         <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
//                         <p className="text-gray-600 mt-1">{order.shippingAddress.addressLine1}</p>
//                         {order.shippingAddress.addressLine2 && (
//                           <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                         )}
//                         <p className="text-gray-600">
//                           {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                         </p>
//                         <p className="text-gray-600">{order.shippingAddress.country}</p>
//                         <p className="text-gray-600 mt-2">Phone: {order.shippingAddress.phone}</p>
//                       </div>
//                     </div>
                    
//                     <div className="space-y-6">
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Seller Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <p className="text-gray-900">{order.seller.businessName}</p>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Payment Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Method:</span>
//                             <span className="font-medium text-gray-900">{order.payment.method.toUpperCase()}</span>
//                           </div>
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Status:</span>
//                             <span className={`font-medium ${
//                               order.payment.status === 'completed' ? 'text-green-600' : 
//                               order.payment.status === 'failed' ? 'text-red-600' : 
//                               'text-yellow-600'
//                             }`}>
//                               {order.payment.status}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Total Paid:</span>
//                             <span className="font-medium text-gray-900">₹{order.totalAmount.toFixed(2)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Tracking Progress */}
//               <div className="lg:col-span-1">
//                 <div className="bg-white rounded-lg p-5 border border-gray-200 sticky top-6">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h2>
                  
//                   <div className="space-y-6">
//                     {/* Progress Bar */}
//                     <div className="relative">
//                       {/* Progress line */}
//                       <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      
//                       {trackingSteps.map((step, index) => (
//                         <div key={step.id} className="relative flex items-start pb-8 last:pb-0">
//                           <div className={`absolute left-5 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                             <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
//                           </div>
                          
//                           <div className={`relative flex items-center justify-center w-10 h-10 rounded-full ${step.current ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${
//                             step.status === 'complete' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
//                           }`}>
//                             {step.status === 'complete' ? (
//                               step.icon
//                             ) : (
//                               <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
//                                 {index + 1}
//                               </span>
//                             )}
//                           </div>
                          
//                           <div className="ml-4 pt-1">
//                             <h3 className={`text-sm font-medium ${
//                               step.current ? 'text-blue-600' : 
//                               step.status === 'complete' ? 'text-gray-900' : 'text-gray-500'
//                             }`}>
//                               {step.name}
//                             </h3>
//                             <p className={`text-xs ${
//                               step.current ? 'text-blue-500' : 'text-gray-400'
//                             }`}>
//                               {step.description}
//                             </p>
//                             {step.current && (
//                               <p className="text-xs text-blue-500 mt-1 font-medium">Current Status</p>
//                             )}
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Tracking Details */}
//                     {order.tracking && order.product.status !== 'cancelled' && (
//                       <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
//                         <h3 className="font-medium text-blue-800 mb-2 flex items-center">
//                           <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
//                           </svg>
//                           Tracking Details
//                         </h3>
//                         <div className="space-y-2 text-sm">
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Carrier:</span>
//                             <span className="font-medium text-blue-900">{order.tracking.carrier || 'Not specified'}</span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Status:</span>
//                             <span className="font-medium text-blue-900 capitalize">{order.tracking.status || 'Not available'}</span>
//                           </div>
//                           {order.tracking.awbNumber && (
//                             <div className="flex justify-between">
//                               <span className="text-blue-700">AWB #:</span>
//                               <span className="font-medium text-blue-900">{order.tracking.awbNumber}</span>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* Other Products in Same Order */}
//             {orderData.otherProductsInSameOrder && orderData.otherProductsInSameOrder.length > 0 && (
//               <div className="p-6 border-t border-gray-200">
//                 <h2 className="text-lg font-semibold text-gray-900 mb-4">Other Products in This Order</h2>
//                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                   {orderData.otherProductsInSameOrder.map((product) => (
//                     <div 
//                       key={product.orderProductId}
//                       onClick={() => navigate(`/orders/${product.orderProductId}`)}
//                       className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
//                     >
//                       <div className="flex items-start gap-4">
//                         <div className="flex-shrink-0">
//                           <img 
//                             src={product.images[0]} 
//                             alt={product.name}
//                             className="w-16 h-16 object-cover rounded-md border border-gray-200"
//                           />
//                         </div>
//                         <div className="flex-grow">
//                           <h3 className="font-medium text-gray-900">{product.name}</h3>
//                           <div className="mt-1 text-sm text-gray-600">
//                             <p>Quantity: {product.quantity}</p>
//                             <p>Price: ₹{product.price.toFixed(2)}</p>
//                             <p>Total: ₹{product.total.toFixed(2)}</p>
//                           </div>
//                         </div>
//                         <div className="flex-shrink-0">
//                           <span className={`px-2 py-1 rounded-full text-xs font-medium ${
//                             product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                             product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                             'bg-blue-100 text-blue-800'
//                           }`}>
//                             {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
//                           </span>
//                         </div>
//                       </div>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default OrderDetails;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Loader from './Loader';
// import ErrorMessage from './ErrorMessage';
// import Header from '../Header/Header';

// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [order, setOrder] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [cancelling, setCancelling] = useState(false);
//   const [showCancelModal, setShowCancelModal] = useState(false);

//   useEffect(() => {
//     const fetchOrderDetails = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
//           {
//             headers: { Authorization: `Bearer ${token}` }
//           }
//         );
//         setOrder(response.data.product);
//       } catch (err) {
//         setError(err.response?.data?.message || "Failed to fetch order details");
//         toast.error("Failed to load order details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const handleCancelOrder = async () => {
//     if (!order) return;
    
//     try {
//       setCancelling(true);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.patch(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/cancel-product-by-buyer/${order.orderId}`,
//         {
//           productId: order.product.productId,
//           variantId: order.product.variantId
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );

//       toast.success(response.data.message);
//       // Refresh order details after cancellation
//       const updatedResponse = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );
//       setOrder(updatedResponse.data.product);
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to cancel order");
//     } finally {
//       setCancelling(false);
//       setShowCancelModal(false);
//     }
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { 
//         id: 'ordered', 
//         name: 'Order Placed', 
//         description: 'Your order has been received',
//         apiStatus: 'pending',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7 7v18" />
//           </svg>
//         )
//       },
//       { 
//         id: 'confirmed', 
//         name: 'Order Confirmed', 
//         description: 'Seller has accepted your order',
//         apiStatus: 'confirmed',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       },
//       { 
//         id: 'shipped', 
//         name: 'Shipped', 
//         description: 'Seller has shipped your item',
//         apiStatus: 'shipped',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'in_transit', 
//         name: 'In Transit', 
//         description: 'Your item is on the way',
//         apiStatus: 'in_transit',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'delivered', 
//         name: 'Delivered', 
//         description: 'Your item has arrived',
//         apiStatus: 'delivered',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       }
//     ];

//     let currentStepIndex = 0;
    
//     if (order) {
//       if (order.product.status === 'cancelled') {
//         // If order is cancelled, show all steps as incomplete
//         return steps.map(step => ({
//           ...step,
//           status: 'incomplete',
//           current: false
//         }));
//       }

//       if (order.product.status === 'confirmed') {
//         currentStepIndex = 1;
//       }

//       if (order.tracking) {
//         switch (order.tracking.status) {
//           case 'shipped':
//             currentStepIndex = 2;
//             break;
//           case 'in_transit':
//             currentStepIndex = 3;
//             break;
//           case 'delivered':
//             currentStepIndex = 4;
//             break;
//           case 'not_shipped':
//             currentStepIndex = order.product.status === 'confirmed' ? 1 : 0;
//             break;
//           default:
//             break;
//         }
//       }
//     }

//     return steps.map((step, index) => ({
//       ...step,
//       status: index <= currentStepIndex ? 'complete' : 'incomplete',
//       current: index === currentStepIndex
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} />;
//   if (!order) return <ErrorMessage message="Order not found" />;

//   const trackingSteps = getTrackingSteps();
//   const canCancel = order.tracking?.status !== 'delivered' && 
//                    order.product.status !== 'cancelled' && 
//                    order.product.status !== 'delivered';

//   return (
//     <>
//       <Header />
//       <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//         <div className="max-w-6xl mx-auto">
//           {/* Cancel Confirmation Modal */}
//           {showCancelModal && (
//             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//               <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
//                 <div className="p-6">
//                   <div className="flex items-center justify-between mb-4">
//                     <h3 className="text-lg font-bold text-gray-900">Confirm Cancellation</h3>
//                     <button 
//                       onClick={() => setShowCancelModal(false)}
//                       className="text-gray-400 hover:text-gray-500"
//                     >
//                       <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                     </button>
//                   </div>
//                   <div className="mb-6">
//                     <div className="flex items-start">
//                       <div className="flex-shrink-0 mt-1">
//                         <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                         </svg>
//                       </div>
//                       <div className="ml-3">
//                         <p className="text-gray-700">
//                           Are you sure you want to cancel this order? 
//                           This action cannot be undone.
//                         </p>
//                         {order.tracking?.status === 'shipped' && (
//                           <p className="text-gray-600 mt-2 text-sm">
//                             Note: Since your order has already been shipped, you may need to return the item to receive a full refund.
//                           </p>
//                         )}
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex justify-end space-x-3">
//                     <button
//                       onClick={() => setShowCancelModal(false)}
//                       className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
//                     >
//                       Go Back
//                     </button>
//                     <button
//                       onClick={handleCancelOrder}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
//                     >
//                       {cancelling ? (
//                         <>
//                           <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                           </svg>
//                           Cancelling...
//                         </>
//                       ) : (
//                         'Confirm Cancellation'
//                       )}
//                     </button>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           )}

//           <div className="mb-6">
//             <button 
//               onClick={() => navigate(-1)}
//               className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
//             >
//               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//               </svg>
//               Back to Orders
//             </button>
//           </div>

//           <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//             <div className="p-6 border-b border-gray-200">
//               <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
//                 <div>
//                   <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
//                   <p className="text-gray-500 mt-1">Order ID: {order.orderId}</p>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <span className={`px-3 py-1 rounded-full text-sm font-medium ${
//                     order.product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                     order.product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                     'bg-blue-100 text-blue-800'
//                   }`}>
//                     {order.product.status.charAt(0).toUpperCase() + order.product.status.slice(1)}
//                   </span>
//                   {canCancel && (
//                     <button
//                       onClick={() => setShowCancelModal(true)}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-white border border-red-500 text-red-600 rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center space-x-2"
//                     >
//                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                         <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                       </svg>
//                       <span>Cancel Order</span>
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
            
//             {/* Cancellation Banner if order is cancelled */}
//             {order.product.status === 'cancelled' && (
//               <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4">
//                 <div className="flex items-center">
//                   <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                   </svg>
//                   <div>
//                     <strong className="font-medium">This order has been cancelled</strong>
//                     <p className="text-sm mt-1">Your refund will be processed according to the seller's policy.</p>
//                   </div>
//                 </div>
//               </div>
//             )}
            
//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
//               {/* Product Information */}
//               <div className="lg:col-span-2 space-y-6">
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h2>
                  
//                   <div className="flex flex-col sm:flex-row gap-5">
//                     <div className="flex-shrink-0">
//                       <img 
//                         src={order.product.images[0]} 
//                         alt={order.product.name}
//                         className="w-32 h-32 object-cover rounded-lg border border-gray-200"
//                       />
//                     </div>
                    
//                     <div className="flex-grow">
//                       <h3 className="text-xl font-medium text-gray-900">{order.product.name}</h3>
//                       <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
//                         <div>
//                           <span className="text-gray-500">Quantity:</span>
//                           <span className="ml-2 font-medium text-gray-900">{order.product.quantity}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Price:</span>
//                           <span className="ml-2 font-medium text-gray-900">₹{order.product.price.toFixed(2)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Total:</span>
//                           <span className="ml-2 font-medium text-gray-900">₹{order.product.total.toFixed(2)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Order Date:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatDate(order.orderCreatedAt)}</span>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Shipping Information */}
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping & Payment</h2>
                  
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div>
//                       <h3 className="font-medium text-gray-900 mb-3">Delivery Address</h3>
//                       <div className="bg-white p-4 rounded-md border border-gray-200">
//                         <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
//                         <p className="text-gray-600 mt-1">{order.shippingAddress.addressLine1}</p>
//                         {order.shippingAddress.addressLine2 && (
//                           <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                         )}
//                         <p className="text-gray-600">
//                           {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                         </p>
//                         <p className="text-gray-600">{order.shippingAddress.country}</p>
//                         <p className="text-gray-600 mt-2">Phone: {order.shippingAddress.phone}</p>
//                       </div>
//                     </div>
                    
//                     <div className="space-y-6">
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Seller Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <p className="text-gray-900">{order.seller.businessName}</p>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Payment Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Method:</span>
//                             <span className="font-medium text-gray-900">{order.payment.method.toUpperCase()}</span>
//                           </div>
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Status:</span>
//                             <span className={`font-medium ${
//                               order.payment.status === 'completed' ? 'text-green-600' : 
//                               order.payment.status === 'failed' ? 'text-red-600' : 
//                               'text-yellow-600'
//                             }`}>
//                               {order.payment.status}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Total Paid:</span>
//                             <span className="font-medium text-gray-900">₹{order.totalAmount.toFixed(2)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Tracking Progress */}
//               <div className="lg:col-span-1">
//                 <div className="bg-white rounded-lg p-5 border border-gray-200 sticky top-6">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h2>
                  
//                   <div className="space-y-6">
//                     {/* Progress Bar */}
//                     <div className="relative">
//                       {/* Progress line */}
//                       <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      
//                       {trackingSteps.map((step, index) => (
//                         <div key={step.id} className="relative flex items-start pb-8 last:pb-0">
//                           <div className={`absolute left-5 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                             <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
//                           </div>
                          
//                           <div className={`relative flex items-center justify-center w-10 h-10 rounded-full ${step.current ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${
//                             step.status === 'complete' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
//                           }`}>
//                             {step.status === 'complete' ? (
//                               step.icon
//                             ) : (
//                               <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
//                                 {index + 1}
//                               </span>
//                             )}
//                           </div>
                          
//                           <div className="ml-4 pt-1">
//                             <h3 className={`text-sm font-medium ${
//                               step.current ? 'text-blue-600' : 
//                               step.status === 'complete' ? 'text-gray-900' : 'text-gray-500'
//                             }`}>
//                               {step.name}
//                             </h3>
//                             <p className={`text-xs ${
//                               step.current ? 'text-blue-500' : 'text-gray-400'
//                             }`}>
//                               {step.description}
//                             </p>
//                             {step.current && (
//                               <p className="text-xs text-blue-500 mt-1 font-medium">Current Status</p>
//                             )}
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Tracking Details */}
//                     {order.tracking && order.product.status !== 'cancelled' && (
//                       <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
//                         <h3 className="font-medium text-blue-800 mb-2 flex items-center">
//                           <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
//                           </svg>
//                           Tracking Details
//                         </h3>
//                         <div className="space-y-2 text-sm">
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Carrier:</span>
//                             <span className="font-medium text-blue-900">{order.tracking.carrier || 'Not specified'}</span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Status:</span>
//                             <span className="font-medium text-blue-900 capitalize">{order.tracking.status || 'Not available'}</span>
//                           </div>
//                           {order.tracking.trackingNumber && (
//                             <div className="flex justify-between">
//                               <span className="text-blue-700">Tracking #:</span>
//                               <span className="font-medium text-blue-900">{order.tracking.trackingNumber}</span>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default OrderDetails;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Loader from './Loader';
// import ErrorMessage from './ErrorMessage';
// import Header from '../Header/Header';

// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [order, setOrder] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [cancelling, setCancelling] = useState(false);

//   useEffect(() => {
//     const fetchOrderDetails = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
//           {
//             headers: { Authorization: `Bearer ${token}` }
//           }
//         );
//         setOrder(response.data.product);
//       } catch (err) {
//         setError(err.response?.data?.message || "Failed to fetch order details");
//         toast.error("Failed to load order details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const handleCancelOrder = async () => {
//     if (!order) return;
    
//     // Check if order can be cancelled (not delivered and not already cancelled)
//     if (order.tracking?.status === 'delivered' || order.product.status === 'cancelled') {
//       toast.error("This order cannot be cancelled");
//       return;
//     }

//     // Confirm cancellation with user
//     const isConfirmed = window.confirm("Are you sure you want to cancel this order?");
//     if (!isConfirmed) return;

//     try {
//       setCancelling(true);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.patch(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/cancel-product-by-buyer/${order.orderId}`,
//         {
//           productId: order.product.productId,
//           variantId: order.product.variantId
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );

//       toast.success(response.data.message);
//       // Refresh order details after cancellation
//       const updatedResponse = await axios.get(
//         `${process.env.REACT_APP_BACKEND_URL}/api/buyer/orderd-product/${orderItemId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );
//       setOrder(updatedResponse.data.product);
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to cancel order");
//     } finally {
//       setCancelling(false);
//     }
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { 
//         id: 'ordered', 
//         name: 'Order Placed', 
//         description: 'Your order has been received',
//         apiStatus: 'pending',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
//           </svg>
//         )
//       },
//       { 
//         id: 'confirmed', 
//         name: 'Order Confirmed', 
//         description: 'Seller has accepted your order',
//         apiStatus: 'confirmed',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       },
//       { 
//         id: 'shipped', 
//         name: 'Shipped', 
//         description: 'Seller has shipped your item',
//         apiStatus: 'shipped',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'in_transit', 
//         name: 'In Transit', 
//         description: 'Your item is on the way',
//         apiStatus: 'in_transit',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
//           </svg>
//         )
//       },
//       { 
//         id: 'delivered', 
//         name: 'Delivered', 
//         description: 'Your item has arrived',
//         apiStatus: 'delivered',
//         icon: (
//           <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//           </svg>
//         )
//       }
//     ];

//     let currentStepIndex = 0;
    
//     if (order) {
//       if (order.product.status === 'cancelled') {
//         // If order is cancelled, show all steps as incomplete
//         return steps.map(step => ({
//           ...step,
//           status: 'incomplete',
//           current: false
//         }));
//       }

//       if (order.product.status === 'confirmed') {
//         currentStepIndex = 1;
//       }

//       if (order.tracking) {
//         switch (order.tracking.status) {
//           case 'shipped':
//             currentStepIndex = 2;
//             break;
//           case 'in_transit':
//             currentStepIndex = 3;
//             break;
//           case 'delivered':
//             currentStepIndex = 4;
//             break;
//           case 'not_shipped':
//             currentStepIndex = order.product.status === 'confirmed' ? 1 : 0;
//             break;
//           default:
//             break;
//         }
//       }
//     }

//     return steps.map((step, index) => ({
//       ...step,
//       status: index <= currentStepIndex ? 'complete' : 'incomplete',
//       current: index === currentStepIndex
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} />;
//   if (!order) return <ErrorMessage message="Order not found" />;

//   const trackingSteps = getTrackingSteps();
//   const canCancel = order.tracking?.status !== 'delivered' && 
//                    order.product.status !== 'cancelled' && 
//                    order.product.status !== 'delivered';

//   return (
//     <>
//       <Header />
//       <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
//         <div className="max-w-6xl mx-auto">
//           <div className="mb-6">
//             <button 
//               onClick={() => navigate(-1)}
//               className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
//             >
//               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//               </svg>
//               Back to Orders
//             </button>
//           </div>

//           <div className="bg-white rounded-xl shadow-sm overflow-hidden">
//             <div className="p-6 border-b border-gray-200">
//               <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
//                 <div>
//                   <h1 className="text-2xl font-bold text-gray-900">Order Details</h1>
//                   <p className="text-gray-500 mt-1">Order ID: {order.orderId}</p>
//                 </div>
//                 <div className="flex items-center space-x-2">
//                   <span className={`px-3 py-1 rounded-full text-sm font-medium ${
//                     order.product.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                     order.product.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                     'bg-blue-100 text-blue-800'
//                   }`}>
//                     {order.product.status.charAt(0).toUpperCase() + order.product.status.slice(1)}
//                   </span>
//                   {canCancel && (
//                     <button
//                       onClick={handleCancelOrder}
//                       disabled={cancelling}
//                       className="px-4 py-2 bg-white border border-red-500 text-red-600 rounded-md hover:bg-red-50 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center space-x-2"
//                     >
//                       {cancelling ? (
//                         <>
//                           <svg className="animate-spin h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                             <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                             <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                           </svg>
//                           <span>Cancelling...</span>
//                         </>
//                       ) : (
//                         <>
//                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                           </svg>
//                           <span>Cancel Order</span>
//                         </>
//                       )}
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
            
//             {/* Cancellation Banner if order is cancelled */}
//             {order.product.status === 'cancelled' && (
//               <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4">
//                 <div className="flex items-center">
//                   <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
//                   </svg>
//                   <div>
//                     <strong className="font-medium">This order has been cancelled</strong>
//                     <p className="text-sm mt-1">Your refund will be processed according to the seller's policy.</p>
//                   </div>
//                 </div>
//               </div>
//             )}
            
//             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6">
//               {/* Product Information */}
//               <div className="lg:col-span-2 space-y-6">
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Product Information</h2>
                  
//                   <div className="flex flex-col sm:flex-row gap-5">
//                     <div className="flex-shrink-0">
//                       <img 
//                         src={order.product.images[0]} 
//                         alt={order.product.name}
//                         className="w-32 h-32 object-cover rounded-lg border border-gray-200"
//                       />
//                     </div>
                    
//                     <div className="flex-grow">
//                       <h3 className="text-xl font-medium text-gray-900">{order.product.name}</h3>
//                       <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
//                         <div>
//                           <span className="text-gray-500">Quantity:</span>
//                           <span className="ml-2 font-medium text-gray-900">{order.product.quantity}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Price:</span>
//                           <span className="ml-2 font-medium text-gray-900">₹{order.product.price.toFixed(2)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Total:</span>
//                           <span className="ml-2 font-medium text-gray-900">₹{order.product.total.toFixed(2)}</span>
//                         </div>
//                         <div>
//                           <span className="text-gray-500">Order Date:</span>
//                           <span className="ml-2 font-medium text-gray-900">{formatDate(order.orderCreatedAt)}</span>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Shipping Information */}
//                 <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping & Payment</h2>
                  
//                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                     <div>
//                       <h3 className="font-medium text-gray-900 mb-3">Delivery Address</h3>
//                       <div className="bg-white p-4 rounded-md border border-gray-200">
//                         <p className="font-medium text-gray-900">{order.shippingAddress.fullName}</p>
//                         <p className="text-gray-600 mt-1">{order.shippingAddress.addressLine1}</p>
//                         {order.shippingAddress.addressLine2 && (
//                           <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                         )}
//                         <p className="text-gray-600">
//                           {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                         </p>
//                         <p className="text-gray-600">{order.shippingAddress.country}</p>
//                         <p className="text-gray-600 mt-2">Phone: {order.shippingAddress.phone}</p>
//                       </div>
//                     </div>
                    
//                     <div className="space-y-6">
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Seller Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <p className="text-gray-900">{order.seller.businessName}</p>
//                         </div>
//                       </div>
                      
//                       <div>
//                         <h3 className="font-medium text-gray-900 mb-3">Payment Information</h3>
//                         <div className="bg-white p-4 rounded-md border border-gray-200">
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Method:</span>
//                             <span className="font-medium text-gray-900">{order.payment.method.toUpperCase()}</span>
//                           </div>
//                           <div className="flex justify-between mb-2">
//                             <span className="text-gray-600">Status:</span>
//                             <span className={`font-medium ${
//                               order.payment.status === 'completed' ? 'text-green-600' : 
//                               order.payment.status === 'failed' ? 'text-red-600' : 
//                               'text-yellow-600'
//                             }`}>
//                               {order.payment.status}
//                             </span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-gray-600">Total Paid:</span>
//                             <span className="font-medium text-gray-900">₹{order.totalAmount.toFixed(2)}</span>
//                           </div>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               {/* Tracking Progress */}
//               <div className="lg:col-span-1">
//                 <div className="bg-white rounded-lg p-5 border border-gray-200 sticky top-6">
//                   <h2 className="text-lg font-semibold text-gray-900 mb-4">Order Status</h2>
                  
//                   <div className="space-y-6">
//                     {/* Progress Bar */}
//                     <div className="relative">
//                       {/* Progress line */}
//                       <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                      
//                       {trackingSteps.map((step, index) => (
//                         <div key={step.id} className="relative flex items-start pb-8 last:pb-0">
//                           <div className={`absolute left-5 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                             <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
//                           </div>
                          
//                           <div className={`relative flex items-center justify-center w-10 h-10 rounded-full ${step.current ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${
//                             step.status === 'complete' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-400'
//                           }`}>
//                             {step.status === 'complete' ? (
//                               step.icon
//                             ) : (
//                               <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>
//                                 {index + 1}
//                               </span>
//                             )}
//                           </div>
                          
//                           <div className="ml-4 pt-1">
//                             <h3 className={`text-sm font-medium ${
//                               step.current ? 'text-blue-600' : 
//                               step.status === 'complete' ? 'text-gray-900' : 'text-gray-500'
//                             }`}>
//                               {step.name}
//                             </h3>
//                             <p className={`text-xs ${
//                               step.current ? 'text-blue-500' : 'text-gray-400'
//                             }`}>
//                               {step.description}
//                             </p>
//                             {step.current && (
//                               <p className="text-xs text-blue-500 mt-1 font-medium">Current Status</p>
//                             )}
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Tracking Details */}
//                     {order.tracking && order.product.status !== 'cancelled' && (
//                       <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
//                         <h3 className="font-medium text-blue-800 mb-2 flex items-center">
//                           <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
//                           </svg>
//                           Tracking Details
//                         </h3>
//                         <div className="space-y-2 text-sm">
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Carrier:</span>
//                             <span className="font-medium text-blue-900">{order.tracking.carrier || 'Not specified'}</span>
//                           </div>
//                           <div className="flex justify-between">
//                             <span className="text-blue-700">Status:</span>
//                             <span className="font-medium text-blue-900 capitalize">{order.tracking.status || 'Not available'}</span>
//                           </div>
//                           {order.tracking.trackingNumber && (
//                             <div className="flex justify-between">
//                               <span className="text-blue-700">Tracking #:</span>
//                               <span className="font-medium text-blue-900">{order.tracking.trackingNumber}</span>
//                             </div>
//                           )}
//                         </div>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default OrderDetails;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Loader from './Loader';
// import ErrorMessage from './ErrorMessage';
// import Header from '../Header/Header';

// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [order, setOrder] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [cancelling, setCancelling] = useState(false);

//   useEffect(() => {
//     const fetchOrderDetails = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           `https://seller-backend-29o9.onrender.com/api/buyer/orderd-product/${orderItemId}`,
//           {
//             headers: { Authorization: `Bearer ${token}` }
//           }
//         );
//         setOrder(response.data.product);
//       } catch (err) {
//         setError(err.response?.data?.message || "Failed to fetch order details");
//         toast.error("Failed to load order details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const handleCancelOrder = async () => {
//     if (!order) return;
    
//     // Check if order can be cancelled (not delivered and not already cancelled)
//     if (order.tracking?.status === 'delivered' || order.product.status === 'cancelled') {
//       toast.error("This order cannot be cancelled");
//       return;
//     }

//     try {
//       setCancelling(true);
//       const token = Cookies.get("ecom_user_token");
//       const response = await axios.post(
//         `https://seller-backend-29o9.onrender.com/api/buyer/cancel-product-by-buyer/${order.orderId}`,
//         {
//           productId: order.product.productId,
//           variantId: order.product.variantId
//         },
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );

//       toast.success(response.data.message);
//       // Refresh order details after cancellation
//       const updatedResponse = await axios.get(
//         `https://seller-backend-29o9.onrender.com/api/buyer/orderd-product/${orderItemId}`,
//         {
//           headers: { Authorization: `Bearer ${token}` }
//         }
//       );
//       setOrder(updatedResponse.data.product);
//     } catch (err) {
//       toast.error(err.response?.data?.message || "Failed to cancel order");
//     } finally {
//       setCancelling(false);
//     }
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { 
//         id: 'ordered', 
//         name: 'Order Placed', 
//         description: 'Your order has been received',
//         apiStatus: 'pending'
//       },
//       { 
//         id: 'confirmed', 
//         name: 'Order Confirmed', 
//         description: 'Seller has accepted your order',
//         apiStatus: 'confirmed'
//       },
//       { 
//         id: 'shipped', 
//         name: 'Shipped', 
//         description: 'Seller has shipped your item',
//         apiStatus: 'shipped'
//       },
//       { 
//         id: 'in_transit', 
//         name: 'In Transit', 
//         description: 'Your item is on the way',
//         apiStatus: 'in_transit'
//       },
//       { 
//         id: 'delivered', 
//         name: 'Delivered', 
//         description: 'Your item has arrived',
//         apiStatus: 'delivered'
//       }
//     ];

//     let currentStepIndex = 0;
    
//     if (order) {
//       if (order.product.status === 'cancelled') {
//         // If order is cancelled, show all steps as incomplete
//         return steps.map(step => ({
//           ...step,
//           status: 'incomplete',
//           current: false
//         }));
//       }

//       if (order.product.status === 'confirmed') {
//         currentStepIndex = 1;
//       }

//       if (order.tracking) {
//         switch (order.tracking.status) {
//           case 'shipped':
//             currentStepIndex = 2;
//             break;
//           case 'in_transit':
//             currentStepIndex = 3;
//             break;
//           case 'delivered':
//             currentStepIndex = 4;
//             break;
//           case 'not_shipped':
//             currentStepIndex = order.product.status === 'confirmed' ? 1 : 0;
//             break;
//           default:
//             break;
//         }
//       }
//     }

//     return steps.map((step, index) => ({
//       ...step,
//       status: index <= currentStepIndex ? 'complete' : 'incomplete',
//       current: index === currentStepIndex
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} />;
//   if (!order) return <ErrorMessage message="Order not found" />;

//   const trackingSteps = getTrackingSteps();
//   const canCancel = order.tracking?.status !== 'delivered' && 
//                    order.product.status !== 'cancelled' && 
//                    order.product.status !== 'delivered';

//   return (
//     <>
//       <Header />
//       <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 max-w-4xl mx-auto mt-6">
//         <button 
//           onClick={() => navigate(-1)}
//           className="mb-4 flex items-center text-blue-600 hover:text-blue-800"
//         >
//           <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//           </svg>
//           Back to Orders
//         </button>

//         <h1 className="text-2xl font-bold text-gray-800 mb-6">Order Details</h1>
        
//         {/* Cancellation Banner if order is cancelled */}
//         {order.product.status === 'cancelled' && (
//           <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
//             <div className="flex items-center">
//               <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//               </svg>
//               <strong>This order has been cancelled</strong>
//             </div>
//           </div>
//         )}
        
//         <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//           {/* Product Information */}
//           <div className="md:col-span-2">
//             <div className="bg-gray-50 rounded-lg p-4 mb-6">
//               <div className="flex justify-between items-start mb-4">
//                 <h2 className="text-lg font-semibold text-gray-800">Product Information</h2>
//                 {canCancel && (
//                   <button
//                     onClick={handleCancelOrder}
//                     disabled={cancelling}
//                     className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400 flex items-center"
//                   >
//                     {cancelling ? (
//                       <>
//                         <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                         </svg>
//                         Cancelling...
//                       </>
//                     ) : (
//                       <>
//                         <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//                         </svg>
//                         Cancel Order
//                       </>
//                     )}
//                   </button>
//                 )}
//               </div>
              
//               <div className="flex flex-col sm:flex-row gap-4">
//                 <div className="flex-shrink-0">
//                   <img 
//                     src={order.product.images[0]} 
//                     alt={order.product.name}
//                     className="w-32 h-32 object-cover rounded-md"
//                   />
//                 </div>
                
//                 <div>
//                   <h3 className="text-xl font-medium text-gray-800">{order.product.name}</h3>
//                   <p className="text-gray-600">Quantity: {order.product.quantity}</p>
//                   <p className="text-gray-600">Price: ₹{order.product.price.toFixed(2)}</p>
//                   <p className="text-gray-600">Total: ₹{order.product.total.toFixed(2)}</p>
//                   <p className="text-gray-600">Order ID: {order.orderId}</p>
//                   <p className="text-gray-600">Placed on: {formatDate(order.orderCreatedAt)}</p>
//                   <p className="text-gray-600">
//                     Product Status: <span className={`font-medium ${
//                       order.product.status === 'cancelled' ? 'text-red-600' : 
//                       order.product.status === 'delivered' ? 'text-green-600' : 
//                       'text-blue-600'
//                     }`}>
//                       {order.product.status}
//                     </span>
//                   </p>
//                 </div>
//               </div>
//             </div>

//             {/* Shipping Information */}
//             <div className="bg-gray-50 rounded-lg p-4 mb-6">
//               <h2 className="text-lg font-semibold text-gray-800 mb-4">Shipping Information</h2>
              
//               <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                 <div>
//                   <h4 className="font-medium text-gray-800">Delivery Address</h4>
//                   <p className="text-gray-600">{order.shippingAddress.fullName}</p>
//                   <p className="text-gray-600">{order.shippingAddress.addressLine1}</p>
//                   {order.shippingAddress.addressLine2 && (
//                     <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                   )}
//                   <p className="text-gray-600">
//                     {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                   </p>
//                   <p className="text-gray-600">{order.shippingAddress.country}</p>
//                   <p className="text-gray-600">Phone: {order.shippingAddress.phone}</p>
//                 </div>
                
//                 <div>
//                   <h4 className="font-medium text-gray-800">Seller Information</h4>
//                   <p className="text-gray-600">{order.seller.businessName}</p>
                  
//                   <h4 className="font-medium text-gray-800 mt-4">Payment Information</h4>
//                   <p className="text-gray-600">Method: {order.payment.method.toUpperCase()}</p>
//                   <p className="text-gray-600">Status: {order.payment.status}</p>
//                   <p className="text-gray-600">Total Paid: ₹{order.totalAmount.toFixed(2)}</p>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Tracking Progress */}
//           <div className="md:col-span-1">
//             <div className="bg-gray-50 rounded-lg p-4 sticky top-4">
//               <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Status</h2>
              
//               <div className="space-y-6">
//                 {/* Progress Bar */}
//                 <div className="relative">
//                   {/* Progress line */}
//                   <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  
//                   {trackingSteps.map((step, index) => (
//                     <div key={step.id} className="relative flex items-start pb-6">
//                       <div className={`absolute left-4 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                         <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
//                       </div>
                      
//                       <div className={`relative flex items-center justify-center w-8 h-8 rounded-full ${step.current ? 'ring-2 ring-blue-600 ring-offset-2' : ''} ${step.status === 'complete' ? 'bg-blue-600' : 'bg-gray-200'}`}>
//                         {step.status === 'complete' ? (
//                           <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                           </svg>
//                         ) : (
//                           <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-600'}`}>
//                             {index + 1}
//                           </span>
//                         )}
//                       </div>
                      
//                       <div className="ml-4">
//                         <h3 className={`text-sm font-medium ${step.current ? 'text-blue-600' : step.status === 'complete' ? 'text-blue-600' : 'text-gray-600'}`}>
//                           {step.name}
//                         </h3>
//                         <p className="text-xs text-gray-500">{step.description}</p>
//                         {step.current && (
//                           <p className="text-xs text-blue-600 mt-1">Current Status</p>
//                         )}
//                       </div>
//                     </div>
//                   ))}
//                 </div>

//                 {/* Tracking Details */}
//                 {order.tracking && order.product.status !== 'cancelled' && (
//                   <div className="bg-white p-3 rounded-md border border-gray-200">
//                     <h3 className="font-medium text-gray-800 mb-2">Tracking Details</h3>
//                     <p className="text-sm text-gray-600">
//                       <span className="font-medium">Carrier:</span> {order.tracking.carrier || 'Not specified'}
//                     </p>
//                     <p className="text-sm text-gray-600">
//                       <span className="font-medium">Status:</span> {order.tracking.status || 'Not available'}
//                     </p>
//                     {order.tracking.trackingNumber && (
//                       <p className="text-sm text-gray-600">
//                         <span className="font-medium">Tracking #:</span> {order.tracking.trackingNumber}
//                       </p>
//                     )}
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// };

// export default OrderDetails;


// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Loader from './Loader';
// import ErrorMessage from './ErrorMessage';
// import Header from '../Header/Header';
// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [order, setOrder] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchOrderDetails = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           `https://seller-backend-29o9.onrender.com/api/buyer/orderd-product/${orderItemId}`,
//           {
//             headers: { Authorization: `Bearer ${token}` }
//           }
//         );
//         console.log("API Response:", response.data); // Debugging log
//         setOrder(response.data.product);
//         console.log("product status",response.data.product.status)
//       } catch (err) {
//         setError(err.response?.data?.message || "Failed to fetch order details");
//         toast.error("Failed to load order details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { 
//         id: 'ordered', 
//         name: 'Order Placed', 
//         description: 'Your order has been received',
//         apiStatus: 'pending'
//       },
//       { 
//         id: 'confirmed', 
//         name: 'Order Confirmed', 
//         description: 'Seller has accepted your order',
//         apiStatus: 'confirmed'
//       },
//       { 
//         id: 'shipped', 
//         name: 'Shipped', 
//         description: 'Seller has shipped your item',
//         apiStatus: 'shipped'
//       },
//       { 
//         id: 'in_transit', 
//         name: 'In Transit', 
//         description: 'Your item is on the way',
//         apiStatus: 'in_transit'
//       },
//       { 
//         id: 'delivered', 
//         name: 'Delivered', 
//         description: 'Your item has arrived',
//         apiStatus: 'delivered'
//       }
//     ];

//     let currentStepIndex = 0; // Start with Order Placed as default
    
//     if (order) {
//       // First check product status
//       if (order.product.status === 'confirmed') {
//         currentStepIndex = 1; // Move to Confirmed if product is confirmed
//       }

//       // Then check tracking status if available
//       if (order.tracking) {
//         switch (order.tracking.status) {
//           case 'shipped':
//             currentStepIndex = 2;
//             break;
//           case 'in_transit':
//             currentStepIndex = 3;
//             break;
//           case 'delivered':
//             currentStepIndex = 4;
//             break;
//           case 'not_shipped':
//             // Only show confirmed if product is actually confirmed
//             currentStepIndex = order.product.status === 'confirmed' ? 1 : 0;
//             break;
//           default:
//             // For any other tracking status, don't change the step
//             break;
//         }
//       }
//     }

//     return steps.map((step, index) => ({
//       ...step,
//       status: index <= currentStepIndex ? 'complete' : 'incomplete',
//       current: index === currentStepIndex
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} />;
//   if (!order) return <ErrorMessage message="Order not found" />;

//   const trackingSteps = getTrackingSteps();

//   return (
//     <>
//     <Header />
//     <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 max-w-4xl mx-auto mt-6">
//       <button 
//         onClick={() => navigate(-1)}
//         className="mb-4 flex items-center text-blue-600 hover:text-blue-800"
//       >
//         <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//         </svg>
//         Back to Orders
//       </button>

//       <h1 className="text-2xl font-bold text-gray-800 mb-6">Order Details</h1>
      
//       <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//         {/* Product Information */}
//         <div className="md:col-span-2">
//           <div className="bg-gray-50 rounded-lg p-4 mb-6">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4">Product Information</h2>
            
//             <div className="flex flex-col sm:flex-row gap-4">
//               <div className="flex-shrink-0">
//                 <img 
//                   src={order.product.images[0]} 
//                   alt={order.product.name}
//                   className="w-32 h-32 object-cover rounded-md"
//                 />
//               </div>
              
//               <div>
//                 <h3 className="text-xl font-medium text-gray-800">{order.product.name}</h3>
//                 <p className="text-gray-600">Quantity: {order.product.quantity}</p>
//                 <p className="text-gray-600">Price: ₹{order.product.price.toFixed(2)}</p>
//                 <p className="text-gray-600">Total: ₹{order.product.total.toFixed(2)}</p>
//                 <p className="text-gray-600">Order ID: {order.orderId}</p>
//                 <p className="text-gray-600">Placed on: {formatDate(order.orderCreatedAt)}</p>
//                 <p className="text-gray-600">Product Status: {order.product.status}</p>
//               </div>
//             </div>
//           </div>

//           {/* Shipping Information */}
//           <div className="bg-gray-50 rounded-lg p-4 mb-6">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4">Shipping Information</h2>
            
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//               <div>
//                 <h4 className="font-medium text-gray-800">Delivery Address</h4>
//                 <p className="text-gray-600">{order.shippingAddress.fullName}</p>
//                 <p className="text-gray-600">{order.shippingAddress.addressLine1}</p>
//                 {order.shippingAddress.addressLine2 && (
//                   <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                 )}
//                 <p className="text-gray-600">
//                   {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                 </p>
//                 <p className="text-gray-600">{order.shippingAddress.country}</p>
//                 <p className="text-gray-600">Phone: {order.shippingAddress.phone}</p>
//               </div>
              
//               <div>
//                 <h4 className="font-medium text-gray-800">Seller Information</h4>
//                 <p className="text-gray-600">{order.seller.businessName}</p>
                
//                 <h4 className="font-medium text-gray-800 mt-4">Payment Information</h4>
//                 <p className="text-gray-600">Method: {order.payment.method.toUpperCase()}</p>
//                 <p className="text-gray-600">Status: {order.payment.status}</p>
//                 <p className="text-gray-600">Total Paid: ₹{order.totalAmount.toFixed(2)}</p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Tracking Progress */}
//         <div className="md:col-span-1">
//           <div className="bg-gray-50 rounded-lg p-4 sticky top-4">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Status</h2>
            
//             <div className="space-y-6">
//               {/* Progress Bar */}
//               <div className="relative">
//                 {/* Progress line */}
//                 <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
//                 {trackingSteps.map((step, index) => (
//                   <div key={step.id} className="relative flex items-start pb-6">
//                     <div className={`absolute left-4 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                       <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
//                     </div>
                    
//                     <div className={`relative flex items-center justify-center w-8 h-8 rounded-full ${step.current ? 'ring-2 ring-blue-600 ring-offset-2' : ''} ${step.status === 'complete' ? 'bg-blue-600' : 'bg-gray-200'}`}>
//                       {step.status === 'complete' ? (
//                         <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                         </svg>
//                       ) : (
//                         <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-600'}`}>
//                           {index + 1}
//                         </span>
//                       )}
//                     </div>
                    
//                     <div className="ml-4">
//                       <h3 className={`text-sm font-medium ${step.current ? 'text-blue-600' : step.status === 'complete' ? 'text-blue-600' : 'text-gray-600'}`}>
//                         {step.name}
//                       </h3>
//                       <p className="text-xs text-gray-500">{step.description}</p>
//                       {step.current && (
//                         <p className="text-xs text-blue-600 mt-1">Current Status</p>
//                       )}
//                     </div>
//                   </div>
//                 ))}
//               </div>

//               {/* Tracking Details */}
//               {order.tracking && (
//                 <div className="bg-white p-3 rounded-md border border-gray-200">
//                   <h3 className="font-medium text-gray-800 mb-2">Tracking Details</h3>
//                   <p className="text-sm text-gray-600">
//                     <span className="font-medium">Carrier:</span> {order.tracking.carrier || 'Not specified'}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <span className="font-medium">Status:</span> {order.tracking.status || 'Not available'}
//                   </p>
//                   {order.tracking.trackingNumber && (
//                     <p className="text-sm text-gray-600">
//                       <span className="font-medium">Tracking #:</span> {order.tracking.trackingNumber}
//                     </p>
//                   )}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//     </>
//   );
// };

// export default OrderDetails;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Loader from './Loader';
// import ErrorMessage from './ErrorMessage';

// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [order, setOrder] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchOrderDetails = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           `https://seller-backend-29o9.onrender.com/api/buyer/orderd-product/${orderItemId}`,
//           {
//             headers: { Authorization: `Bearer ${token}` }
//           }
//         );
//         setOrder(response.data.product);
//       } catch (err) {
//         setError(err.response?.data?.message || "Failed to fetch order details");
//         toast.error("Failed to load order details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { 
//         id: 'ordered', 
//         name: 'Order Placed', 
//         description: 'Your order has been received',
//         apiStatus: 'pending'
//       },
//       { 
//         id: 'confirmed', 
//         name: 'Order Confirmed', 
//         description: 'Seller has accepted your order',
//         apiStatus: 'confirmed'
//       },
//       { 
//         id: 'shipped', 
//         name: 'Shipped', 
//         description: 'Seller has shipped your item',
//         apiStatus: 'shipped'
//       },
//       { 
//         id: 'in_transit', 
//         name: 'In Transit', 
//         description: 'Your item is on the way',
//         apiStatus: 'in_transit'
//       },
//       { 
//         id: 'delivered', 
//         name: 'Delivered', 
//         description: 'Your item has arrived',
//         apiStatus: 'delivered'
//       }
//     ];

//     // Determine current status based on your business logic
//     let currentStepIndex = 0;
    
//     if (order) {
//       // Check product status first (pending/confirmed)
//       if (order.product.status === 'confirmed') {
//         currentStepIndex = 1;
//       } else if (order.product.status === 'pending') {
//         currentStepIndex = 0;
//       }
      
//       // Then check tracking status if available
//       if (order.tracking) {
//         switch (order.tracking.status) {
//           case 'shipped':
//             currentStepIndex = 2;
//             break;
//           case 'in_transit':
//             currentStepIndex = 3;
//             break;
//           case 'delivered':
//             currentStepIndex = 4;
//             break;
//           case 'not_shipped':
//             // If not shipped but status is confirmed, stay at confirmed
//             if (order.product.status === 'confirmed') {
//               currentStepIndex = 1;
//             }
//             break;
//           default:
//             break;
//         }
//       }
//     }

//     return steps.map((step, index) => ({
//       ...step,
//       status: index <= currentStepIndex ? 'complete' : 'incomplete',
//       current: index === currentStepIndex
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} />;
//   if (!order) return <ErrorMessage message="Order not found" />;

//   const trackingSteps = getTrackingSteps();

//   return (
//     <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 max-w-4xl mx-auto">
//       <button 
//         onClick={() => navigate(-1)}
//         className="mb-4 flex items-center text-blue-600 hover:text-blue-800"
//       >
//         <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//         </svg>
//         Back to Orders
//       </button>

//       <h1 className="text-2xl font-bold text-gray-800 mb-6">Order Details</h1>
      
//       <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//         {/* Product Information */}
//         <div className="md:col-span-2">
//           <div className="bg-gray-50 rounded-lg p-4 mb-6">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4">Product Information</h2>
            
//             <div className="flex flex-col sm:flex-row gap-4">
//               <div className="flex-shrink-0">
//                 <img 
//                   src={order.product.images[0]} 
//                   alt={order.product.name}
//                   className="w-32 h-32 object-cover rounded-md"
//                 />
//               </div>
              
//               <div>
//                 <h3 className="text-xl font-medium text-gray-800">{order.product.name}</h3>
//                 <p className="text-gray-600">Quantity: {order.product.quantity}</p>
//                 <p className="text-gray-600">Price: ₹{order.product.price.toFixed(2)}</p>
//                 <p className="text-gray-600">Total: ₹{order.product.total.toFixed(2)}</p>
//                 <p className="text-gray-600">Order ID: {order.orderId}</p>
//                 <p className="text-gray-600">Placed on: {formatDate(order.orderCreatedAt)}</p>
//                 <p className="text-gray-600">Product Status: {order.product.status}</p>
//               </div>
//             </div>
//           </div>

//           {/* Shipping Information */}
//           <div className="bg-gray-50 rounded-lg p-4 mb-6">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4">Shipping Information</h2>
            
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//               <div>
//                 <h4 className="font-medium text-gray-800">Delivery Address</h4>
//                 <p className="text-gray-600">{order.shippingAddress.fullName}</p>
//                 <p className="text-gray-600">{order.shippingAddress.addressLine1}</p>
//                 {order.shippingAddress.addressLine2 && (
//                   <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                 )}
//                 <p className="text-gray-600">
//                   {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                 </p>
//                 <p className="text-gray-600">{order.shippingAddress.country}</p>
//                 <p className="text-gray-600">Phone: {order.shippingAddress.phone}</p>
//               </div>
              
//               <div>
//                 <h4 className="font-medium text-gray-800">Seller Information</h4>
//                 <p className="text-gray-600">{order.seller.businessName}</p>
                
//                 <h4 className="font-medium text-gray-800 mt-4">Payment Information</h4>
//                 <p className="text-gray-600">Method: {order.payment.method.toUpperCase()}</p>
//                 <p className="text-gray-600">Status: {order.payment.status}</p>
//                 <p className="text-gray-600">Total Paid: ₹{order.totalAmount.toFixed(2)}</p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Tracking Progress */}
//         <div className="md:col-span-1">
//           <div className="bg-gray-50 rounded-lg p-4 sticky top-4">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Status</h2>
            
//             <div className="space-y-6">
//               {/* Progress Bar */}
//               <div className="relative">
//                 {/* Progress line */}
//                 <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
//                 {trackingSteps.map((step, index) => (
//                   <div key={step.id} className="relative flex items-start pb-6">
//                     <div className={`absolute left-4 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                       <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
//                     </div>
                    
//                     <div className={`relative flex items-center justify-center w-8 h-8 rounded-full ${step.current ? 'ring-2 ring-blue-600 ring-offset-2' : ''} ${step.status === 'complete' ? 'bg-blue-600' : 'bg-gray-200'}`}>
//                       {step.status === 'complete' ? (
//                         <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                         </svg>
//                       ) : (
//                         <span className={`${step.current ? 'text-blue-600 font-medium' : 'text-gray-600'}`}>
//                           {index + 1}
//                         </span>
//                       )}
//                     </div>
                    
//                     <div className="ml-4">
//                       <h3 className={`text-sm font-medium ${step.current ? 'text-blue-600' : step.status === 'complete' ? 'text-blue-600' : 'text-gray-600'}`}>
//                         {step.name}
//                       </h3>
//                       <p className="text-xs text-gray-500">{step.description}</p>
//                       {step.current && (
//                         <p className="text-xs text-blue-600 mt-1">Current Status</p>
//                       )}
//                     </div>
//                   </div>
//                 ))}
//               </div>

//               {/* Tracking Details */}
//               {order.tracking && (
//                 <div className="bg-white p-3 rounded-md border border-gray-200">
//                   <h3 className="font-medium text-gray-800 mb-2">Tracking Details</h3>
//                   <p className="text-sm text-gray-600">
//                     <span className="font-medium">Carrier:</span> {order.tracking.carrier || 'Not specified'}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     <span className="font-medium">Status:</span> {order.tracking.status || 'Not available'}
//                   </p>
//                   {order.tracking.trackingNumber && (
//                     <p className="text-sm text-gray-600">
//                       <span className="font-medium">Tracking #:</span> {order.tracking.trackingNumber}
//                     </p>
//                   )}
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OrderDetails;

// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import Cookies from 'js-cookie';
// import { useParams, useNavigate } from 'react-router-dom';
// import { toast } from 'react-toastify';
// import Loader from './Loader';
// import ErrorMessage from './ErrorMessage';

// const OrderDetails = () => {
//   const { orderItemId } = useParams();
//   const navigate = useNavigate();
//   const [order, setOrder] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchOrderDetails = async () => {
//       try {
//         const token = Cookies.get("ecom_user_token");
//         const response = await axios.get(
//           `https://seller-backend-29o9.onrender.com/api/buyer/orderd-product/${orderItemId}`,
//           {
//             headers: { Authorization: `Bearer ${token}` }
//           }
//         );
//         setOrder(response.data.product);
//       } catch (err) {
//         setError(err.response?.data?.message || "Failed to fetch order details");
//         toast.error("Failed to load order details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchOrderDetails();
//   }, [orderItemId]);

//   const formatDate = (dateString) => {
//     if (!dateString) return 'Not available';
//     const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
//     return new Date(dateString).toLocaleDateString('en-US', options);
//   };

//   const getTrackingSteps = () => {
//     const steps = [
//       { id: 'ordered', name: 'Ordered', description: 'Your order has been placed' },
//       { id: 'confirmed', name: 'Confirmed', description: 'Seller has confirmed your order' },
//       { id: 'shipped', name: 'Shipped', description: 'Your item has been shipped' },
//       { id: 'in_transit', name: 'In Transit', description: 'Your item is on the way' },
//       { id: 'delivered', name: 'Delivered', description: 'Your item has been delivered' }
//     ];

//     // Determine current status - prioritize tracking status over product status
//     let currentStatus = order?.tracking?.status || order?.product?.status || 'ordered';
    
//     // Map status to our steps
//     if (currentStatus === 'not_shipped') currentStatus = 'confirmed';
//     if (currentStatus === 'pending') currentStatus = 'ordered';

//     return steps.map(step => ({
//       ...step,
//       status: steps.findIndex(s => s.id === currentStatus) >= steps.findIndex(s => s.id === step.id) 
//         ? 'complete' 
//         : 'incomplete'
//     }));
//   };

//   if (loading) return <Loader fullScreen />;
//   if (error) return <ErrorMessage message={error} />;
//   if (!order) return <ErrorMessage message="Order not found" />;

//   const trackingSteps = getTrackingSteps();

//   return (
//     <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 max-w-4xl mx-auto">
//       <button 
//         onClick={() => navigate(-1)}
//         className="mb-4 flex items-center text-blue-600 hover:text-blue-800"
//       >
//         <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
//         </svg>
//         Back to Orders
//       </button>

//       <h1 className="text-2xl font-bold text-gray-800 mb-6">Order Details</h1>
      
//       <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
//         {/* Product Information */}
//         <div className="md:col-span-2">
//           <div className="bg-gray-50 rounded-lg p-4 mb-6">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4">Product Information</h2>
            
//             <div className="flex flex-col sm:flex-row gap-4">
//               <div className="flex-shrink-0">
//                 <img 
//                   src={order.product.images[0]} 
//                   alt={order.product.name}
//                   className="w-32 h-32 object-cover rounded-md"
//                 />
//               </div>
              
//               <div>
//                 <h3 className="text-xl font-medium text-gray-800">{order.product.name}</h3>
//                 <p className="text-gray-600">Quantity: {order.product.quantity}</p>
//                 <p className="text-gray-600">Price: ₹{order.product.price.toFixed(2)}</p>
//                 <p className="text-gray-600">Total: ₹{order.product.total.toFixed(2)}</p>
//                 <p className="text-gray-600">Order ID: {order.orderId}</p>
//                 <p className="text-gray-600">Placed on: {formatDate(order.orderCreatedAt)}</p>
//               </div>
//             </div>
//           </div>

//           {/* Shipping Information */}
//           <div className="bg-gray-50 rounded-lg p-4 mb-6">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4">Shipping Information</h2>
            
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//               <div>
//                 <h4 className="font-medium text-gray-800">Delivery Address</h4>
//                 <p className="text-gray-600">{order.shippingAddress.fullName}</p>
//                 <p className="text-gray-600">{order.shippingAddress.addressLine1}</p>
//                 {order.shippingAddress.addressLine2 && (
//                   <p className="text-gray-600">{order.shippingAddress.addressLine2}</p>
//                 )}
//                 <p className="text-gray-600">
//                   {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
//                 </p>
//                 <p className="text-gray-600">{order.shippingAddress.country}</p>
//                 <p className="text-gray-600">Phone: {order.shippingAddress.phone}</p>
//               </div>
              
//               <div>
//                 <h4 className="font-medium text-gray-800">Seller Information</h4>
//                 <p className="text-gray-600">{order.seller.businessName}</p>
                
//                 <h4 className="font-medium text-gray-800 mt-4">Payment Information</h4>
//                 <p className="text-gray-600">Method: {order.payment.method.toUpperCase()}</p>
//                 <p className="text-gray-600">Status: {order.payment.status}</p>
//                 <p className="text-gray-600">Total Paid: ₹{order.totalAmount.toFixed(2)}</p>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Tracking Progress */}
//         <div className="md:col-span-1">
//           <div className="bg-gray-50 rounded-lg p-4 sticky top-4">
//             <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Status</h2>
            
//             <div className="space-y-6">
//               {/* Progress Bar */}
//               <div className="relative">
//                 {/* Progress line */}
//                 <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                
//                 {trackingSteps.map((step, index) => (
//                   <div key={step.id} className="relative flex items-start pb-6">
//                     <div className={`absolute left-4 -ml-0.5 w-1 h-full ${index === trackingSteps.length - 1 ? 'hidden' : ''}`}>
//                       <div className={`h-full w-0.5 ${step.status === 'complete' ? 'bg-blue-600' : 'bg-gray-200'}`}></div>
//                     </div>
                    
//                     <div className={`relative flex items-center justify-center w-8 h-8 rounded-full ${step.status === 'complete' ? 'bg-blue-600' : 'bg-gray-200'}`}>
//                       {step.status === 'complete' ? (
//                         <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
//                         </svg>
//                       ) : (
//                         <span className="text-gray-600">{index + 1}</span>
//                       )}
//                     </div>
                    
//                     <div className="ml-4">
//                       <h3 className={`text-sm font-medium ${step.status === 'complete' ? 'text-blue-600' : 'text-gray-600'}`}>
//                         {step.name}
//                       </h3>
//                       <p className="text-xs text-gray-500">{step.description}</p>
//                     </div>
//                   </div>
//                 ))}
//               </div>

//               {/* Tracking Details */}
//               {order.tracking && (
//                 <div>
//                   <h3 className="font-medium text-gray-800 mb-2">Tracking Details</h3>
//                   <p className="text-sm text-gray-600">
//                     Carrier: {order.tracking.carrier || 'Not specified'}
//                   </p>
//                   <p className="text-sm text-gray-600">
//                     Status: {order.tracking.status || 'Not available'}
//                   </p>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default OrderDetails;